import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Layout,
  Tabs,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Tag,
  Popconfirm,
  Upload,
  Image,
  DatePicker,
  message,
  Tooltip,
  Badge,
  Descriptions,
  Typography,
  Row,
  Col,
  Card,
  App,
  Alert,
  Divider,
  Radio,
  Timeline,
  Steps,
  Segmented,
} from 'antd';
import JsBarcode from 'jsbarcode';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  BarcodeOutlined,
  CopyOutlined,
  FilterOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  StopOutlined,
  InboxOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  AimOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  KeyOutlined,
  SyncOutlined,
  SwapOutlined,
  ClockCircleOutlined,
  ShoppingCartOutlined,
  FilePdfOutlined,
  CheckOutlined,
  SearchOutlined,
  ScanOutlined,
  AppstoreTwoTone,
  CarryOutTwoTone,
  ContainerTwoTone,
  InteractionTwoTone,
  EnvironmentTwoTone,
  DatabaseTwoTone,
  ProfileTwoTone,
} from '@ant-design/icons';
import NavigationBar from '../../components/NavigationBar';
import { useAuth } from '../../context/AuthContext';
import inventoryService from '../../services/inventoryService';
import { getPrinter, printLabel, testPrinterConnection } from '../../utils/zebraPrint';
import { API_ORIGIN } from '../../config';
import DOTheme from '../../components/directOrders/doTheme';
import '../../styles/inventory.css';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const IMAGE_BASE = `${API_ORIGIN}`;

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—';

const lineItemId = (line) =>
  line?.item_id ?? line?.itemId ?? line?.product_id ?? line?.productId ?? null;

const lineItemName = (line) =>
  line?.name || line?.item_name || line?.itemName || line?.product_name || line?.productName
    || (lineItemId(line) != null ? `Item #${lineItemId(line)}` : 'Unknown item');

const typeTagColor = { in: 'green', out: 'red', adjust: 'blue', transfer: 'purple' };
const statusOrder = { active: 0, in_progress: 1, out: 2, returned: 3, cancelled: 4 };

// Labels match the booking PDF. Covers booking statuses and line statuses.
const BOOKING_STATUS = {
  active:      { label: 'Awaiting dispatch', tone: 'accent' },
  in_progress: { label: 'In progress',       tone: 'warn' },
  out:         { label: 'Dispatched',        tone: 'violet' },
  returned:    { label: 'Returned',          tone: 'ok' },
  completed:   { label: 'Completed',         tone: 'ok' },
  cancelled:   { label: 'Cancelled',         tone: 'muted' },
};

const renderBookingStatus = (s) => {
  const def = BOOKING_STATUS[s] || { label: s || '—', tone: 'muted' };
  return <span className={`inv-status is-${def.tone}`}>{def.label}</span>;
};

const fmtRange = (start, end) => {
  if (!start || !end) return null;
  const s = dayjs(start);
  const e = dayjs(end);
  return s.isSame(e, 'year') ? `${s.format('D MMM')} to ${e.format('D MMM YYYY')}` : `${fmtDate(start)} to ${fmtDate(end)}`;
};
const typeIcon = {
  in: <ArrowDownOutlined />,
  out: <ArrowUpOutlined />,
  adjust: <AimOutlined />,
  transfer: <SwapOutlined />,
};

// ─── Location splits (multi-location bookings) ────────────────────────────────

const splitsTotal = (splits) => splits.reduce((s, r) => s + (Number(r.quantity) || 0), 0);

const splitsComplete = (splits) =>
  splits.length > 0 && splits.every(r =>
    r.location_id != null && (Number(r.quantity) || 0) >= 1 && r.start_date && r.end_date);

const fmtYmd = (d) => (d ? dayjs(d).format('YYYY-MM-DD') : undefined);

// Normalise a line's locations[] (from the API) into editable split rows
const splitsFromLine = (line) => {
  const fbStart = fmtYmd(line?.start_date);
  const fbEnd = fmtYmd(line?.end_date);
  const locs = line?.locations;
  if (Array.isArray(locs) && locs.length > 0) {
    return locs.map(l => ({
      location_id: l.location_id,
      quantity: l.quantity,
      start_date: fmtYmd(l.start_date) || fbStart,
      end_date: fmtYmd(l.end_date) || fbEnd,
    }));
  }
  // Back-compat: single-location line
  if (line?.location_id != null) {
    return [{ location_id: line.location_id, quantity: line.quantity ?? 1, start_date: fbStart, end_date: fbEnd }];
  }
  return [{ location_id: undefined, quantity: line?.quantity ?? 1, start_date: fbStart, end_date: fbEnd }];
};

// Build the API location-split payload from editable rows
const splitsToPayload = (splits) =>
  splits.map(s => ({
    location_id: s.location_id,
    quantity: s.quantity,
    start_date: s.start_date,
    end_date: s.end_date,
  }));

const renderLocationTags = (locations) => {
  if (!Array.isArray(locations) || locations.length === 0) return <Text type="secondary">—</Text>;
  return (
    <Space size={[4, 4]} wrap>
      {locations.map((l, i) => (
        <Tooltip
          key={l.location_id ?? i}
          title={l.start_date && l.end_date ? `${fmtDate(l.start_date)} → ${fmtDate(l.end_date)}` : undefined}
        >
          <Tag icon={<EnvironmentOutlined />}>
            {l.location_name || l.name || `#${l.location_id}`} ×{l.quantity}
          </Tag>
        </Tooltip>
      ))}
    </Space>
  );
};

// Live catalogue/booking invalidation signals over SSE. Callbacks are kept in
// refs so the stream is opened once per mount regardless of identity churn,
// and closed automatically when the owning component unmounts.
const useInventoryEvents = ({ onItem, onBooking, onLocation } = {}, enabled = true) => {
  const onItemRef = useRef(onItem);
  const onBookingRef = useRef(onBooking);
  const onLocationRef = useRef(onLocation);
  onItemRef.current = onItem;
  onBookingRef.current = onBooking;
  onLocationRef.current = onLocation;

  useEffect(() => {
    if (!enabled) return;
    const es = inventoryService.subscribeToEvents({
      onItem: (payload) => onItemRef.current?.(payload),
      onBooking: (payload) => onBookingRef.current?.(payload),
      onLocation: (payload) => onLocationRef.current?.(payload),
    });
    return () => es.close();
  }, [enabled]);
};

// Shared fetch of FM-selectable destination locations
const useReservationLocations = (open) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    return inventoryService.getReservationLocations()
      .then(res => {
        const list = Array.isArray(res) ? res : res.locations || [];
        setLocations(list);
        return list;
      })
      .catch(() => { message.error('Could not load locations'); return []; })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (open) reload(); }, [open, reload]);

  // While the picker is open, pick up locations the moment they're approved
  // (or drop off if declined) instead of requiring a modal reopen.
  useInventoryEvents({ onLocation: () => reload() }, open);

  return { locations, loading, reload };
};

// Controlled editor for a single product's location split rows.
// Each row carries its own date window, constrained to the booking window.
const LocationSplitEditor = ({ splits, onChange, locations, locationsLoading, targetTotal, windowStart, windowEnd }) => {
  const total = splitsTotal(splits);
  const mismatch = targetTotal != null && total !== targetTotal;
  const ws = windowStart ? dayjs(windowStart) : null;
  const we = windowEnd ? dayjs(windowEnd) : null;
  const datesReady = !!(ws && we);

  const updateRow = (idx, patch) => onChange(splits.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addRow = () => onChange([...splits, {
    location_id: undefined,
    quantity: 1,
    start_date: ws ? ws.format('YYYY-MM-DD') : undefined,
    end_date: we ? we.format('YYYY-MM-DD') : undefined,
  }]);
  const removeRow = (idx) => onChange(splits.filter((_, i) => i !== idx));

  const disabledDate = (d) => {
    if (!d) return false;
    if (ws && d.isBefore(ws, 'day')) return true;
    if (we && d.isAfter(we, 'day')) return true;
    return false;
  };

  return (
    <div>
      {splits.map((row, idx) => {
        const chosenElsewhere = new Set(
          splits.filter((_, i) => i !== idx).map(r => r.location_id).filter(v => v != null)
        );
        const datesMissing = !row.start_date || !row.end_date;
        return (
          <div key={idx} style={{ marginBottom: 10, paddingBottom: 8, borderBottom: '1px dashed #f0f0f0' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Select
                style={{ flex: 1 }}
                placeholder="Location"
                loading={locationsLoading}
                value={row.location_id}
                onChange={(v) => updateRow(idx, { location_id: v })}
                options={locations
                  .filter(l => !chosenElsewhere.has(l.location_id))
                  .map(l => ({ value: l.location_id, label: l.name }))}
                showSearch
                optionFilterProp="label"
                status={row.location_id == null ? 'error' : undefined}
              />
              <InputNumber
                min={1}
                value={row.quantity}
                onChange={(v) => updateRow(idx, { quantity: v || 1 })}
                style={{ width: 80 }}
              />
              <Button type="text" danger disabled={splits.length === 1} onClick={() => removeRow(idx)}>×</Button>
            </div>
            <div style={{ marginTop: 6 }}>
              <RangePicker
                size="small"
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                disabled={!datesReady}
                disabledDate={disabledDate}
                value={row.start_date && row.end_date ? [dayjs(row.start_date), dayjs(row.end_date)] : null}
                onChange={(dates) => updateRow(idx, {
                  start_date: dates?.[0] ? dates[0].format('YYYY-MM-DD') : undefined,
                  end_date: dates?.[1] ? dates[1].format('YYYY-MM-DD') : undefined,
                })}
                placeholder={datesReady ? ['Used from', 'Used until'] : ['Set booking dates first', '']}
                status={datesReady && datesMissing ? 'error' : undefined}
              />
            </div>
          </div>
        );
      })}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
        <Button size="small" icon={<PlusOutlined />} onClick={addRow}>Split across location</Button>
        <Text type={mismatch ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
          {targetTotal != null ? `Allocated ${total} / ${targetTotal}` : `Total: ${total}`}
        </Text>
      </div>
    </div>
  );
};

// ─── Booking PDF (server-rendered, matches Direct Order house style) ──────────

const openBookingPdf = async (bookingId, { download = false } = {}) => {
  // Open the tab synchronously on the click so browsers don't treat it as a popup.
  const win = download ? null : window.open('', '_blank');
  try {
    const blob = await inventoryService.getBookingPdfBlob(bookingId, download);
    const url = window.URL.createObjectURL(blob);
    if (download) {
      const link = document.createElement('a');
      link.href = url;
      link.download = `booking-${bookingId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (win) {
      win.location.href = url;
    } else {
      window.open(url, '_blank');
    }
    // Revoke well after the tab/download has had time to load it.
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  } catch (err) {
    win?.close();
    message.error('Could not load the booking PDF');
  }
};

const ViewPdfButton = ({ bookingId, size = 'small' }) => {
  const [loading, setLoading] = useState(false);
  const handleClick = async () => {
    setLoading(true);
    await openBookingPdf(bookingId);
    setLoading(false);
  };
  return (
    <Button size={size} icon={<FilePdfOutlined />} loading={loading} onClick={handleClick}>
      PDF
    </Button>
  );
};

// ─── Barcode components ───────────────────────────────────────────────────────

const BarcodeDisplay = ({ value, width = 2, height = 60, fontSize = 13 }) => {
  const svgRef = useRef(null);

  useEffect(() => {
    if (svgRef.current && value) {
      JsBarcode(svgRef.current, value, {
        format: 'CODE128',
        width,
        height,
        fontSize,
        margin: 10,
        background: '#ffffff',
        lineColor: '#000000',
        displayValue: true,
      });
    }
  }, [value, width, height, fontSize]);

  if (!value) return null;
  return <svg ref={svgRef} style={{ width: '100%', height: 'auto', display: 'block' }} />;
};

const BarcodeModal = ({ barcode, itemName, open, onClose }) => {
  const [zebraStatus, setZebraStatus] = useState('idle'); // idle | printing | done | error
  const [testStatus, setTestStatus] = useState('idle');   // idle | testing | ok | error
  const [labelQty, setLabelQty] = useState(1);

  const handleTestConnection = async () => {
    setTestStatus('testing');
    try {
      const printer = await getPrinter();
      if (!printer) {
        message.warning('No Zebra printer found. Is Zebra Browser Print running?', 5);
        setTestStatus('idle');
        return;
      }
      await testPrinterConnection(printer);
      setTestStatus('ok');
      message.success('~WC sent — check if a configuration label came out of the printer', 6);
      setTimeout(() => setTestStatus('idle'), 4000);
    } catch (e) {
      message.error(`Test failed: ${e.message}`, 5);
      setTestStatus('error');
      setTimeout(() => setTestStatus('idle'), 3000);
    }
  };

  const handleZebraPrint = async () => {
    setZebraStatus('printing');
    try {
      const printer = await getPrinter();
      if (!printer) {
        message.warning(
          'No Zebra printer found. Make sure Zebra Browser Print is running, ' +
          'then open https://localhost:9101 in this browser to accept the certificate.',
          6
        );
        setZebraStatus('idle');
        return;
      }
      await printLabel(printer, barcode, itemName || '', labelQty);
      setZebraStatus('done');
      message.success('Label sent to printer');
      setTimeout(() => setZebraStatus('idle'), 2500);
    } catch (e) {
      message.error(`Print failed: ${e.message}`, 5);
      setZebraStatus('error');
      setTimeout(() => setZebraStatus('idle'), 2500);
    }
  };

  return (
    <Modal
      title={null}
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={440}
    >
      {/* Header */}
      <div style={{ textAlign: 'center', paddingBottom: 4 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Print Label
        </Typography.Title>
        {itemName && (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {itemName}
          </Typography.Text>
        )}
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Barcode preview */}
      <div style={{
        background: '#fafafa',
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        padding: '20px 16px',
        textAlign: 'center',
      }}>
        <BarcodeDisplay value={barcode} width={2} height={80} fontSize={14} />
      </div>

      {/* Quantity row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 16,
        padding: '12px 16px',
        background: '#f5f5f5',
        borderRadius: 8,
      }}>
        <Typography.Text strong>Copies</Typography.Text>
        <InputNumber
          min={1}
          max={999}
          value={labelQty}
          onChange={(v) => setLabelQty(v ?? 1)}
          style={{ width: 90 }}
        />
      </div>

      <Divider style={{ margin: '16px 0 12px' }} />

      {/* Actions */}
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Button
          size="small"
          loading={testStatus === 'testing'}
          disabled={testStatus === 'testing'}
          onClick={handleTestConnection}
          title="Sends ~WC to confirm ZBP can reach the printer"
        >
          {testStatus === 'ok' ? '✓ Connected' : testStatus === 'error' ? 'Test Failed' : 'Test Connection'}
        </Button>
        <Space>
          <Button
            onClick={() => { navigator.clipboard.writeText(barcode); message.success('Copied!'); }}
          >
            Copy Code
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={zebraStatus === 'printing'}
            disabled={zebraStatus === 'printing'}
            onClick={handleZebraPrint}
          >
            {zebraStatus === 'done' ? '✓ Sent' : zebraStatus === 'error' ? 'Failed' : 'Print Label'}
          </Button>
        </Space>
      </Space>
    </Modal>
  );
};

// ─── Items Tab ───────────────────────────────────────────────────────────────

const ItemsTab = () => {
  const { modal } = App.useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Item create/edit modal
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm] = Form.useForm();
  const [imageFileList, setImageFileList] = useState([]);
  const [savingItem, setSavingItem] = useState(false);

  // Stock adjust modal
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [adjustingItem, setAdjustingItem] = useState(null);
  const [adjustForm] = Form.useForm();
  const [savingAdjust, setSavingAdjust] = useState(false);
  const [adjustType, setAdjustType] = useState(null);
  const [adjustDirection, setAdjustDirection] = useState('increase');
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  // Barcode view modal
  const [barcodeModalItem, setBarcodeModalItem] = useState(null);

  const fetchLocations = useCallback(async () => {
    try {
      setLocationsLoading(true);
      const r = await inventoryService.getLocations();
      setLocations(Array.isArray(r) ? r : r.locations || []);
    } catch {
      message.error('Failed to load locations');
    } finally {
      setLocationsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await inventoryService.getItems();
      setItems(Array.isArray(res) ? res : res.items || []);
    } catch (err) {
      message.error('Failed to load items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openCreateModal = () => {
    setEditingItem(null);
    itemForm.resetFields();
    setImageFileList([]);
    setItemModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    itemForm.setFieldsValue({ name: item.name, description: item.description });
    setImageFileList([]);
    setItemModalOpen(true);
  };

  const handleItemSave = async () => {
    try {
      const values = await itemForm.validateFields();
      setSavingItem(true);

      const formData = new FormData();
      formData.append('name', values.name);
      if (values.description) formData.append('description', values.description);
      if (imageFileList.length > 0 && imageFileList[0].originFileObj) {
        formData.append('image', imageFileList[0].originFileObj);
      }

      if (editingItem) {
        await inventoryService.updateItem(editingItem.item_id, formData);
        message.success('Item updated');
      } else {
        await inventoryService.createItem(formData);
        message.success('Item created');
      }

      setItemModalOpen(false);
      fetchItems();
    } catch (err) {
      if (err?.errorFields) return; // Ant Design validation error
      message.error(err.message || 'Failed to save item');
    } finally {
      setSavingItem(false);
    }
  };

  const handleDelete = async (item) => {
    try {
      await inventoryService.deleteItem(item.item_id);
      message.success(`"${item.name}" deleted`);
      fetchItems();
    } catch (err) {
      message.error('Failed to delete item');
    }
  };

  const openAdjustModal = (item) => {
    setAdjustingItem(item);
    adjustForm.resetFields();
    setAdjustType(null);
    setAdjustDirection('increase');
    setAdjustModalOpen(true);
    fetchLocations();
  };

  const handleAdjustSave = async () => {
    try {
      const values = await adjustForm.validateFields();
      setSavingAdjust(true);
      const signedQty =
        values.transaction_type === 'adjust' && adjustDirection === 'decrease'
          ? -values.quantity
          : values.quantity;
      const res = await inventoryService.adjustStock({
        itemId: adjustingItem.item_id,
        quantity: signedQty,
        transaction_type: values.transaction_type,
        notes: values.notes || '',
        from_location_id: values.from_location_id ?? null,
        to_location_id: values.to_location_id ?? null,
      });
      if (res.success === false) {
        message.error(res.error || 'Adjustment failed');
      } else {
        message.success('Stock adjusted');
        setAdjustModalOpen(false);
        fetchItems();
      }
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Failed to adjust stock');
    } finally {
      setSavingAdjust(false);
    }
  };

  const beforeUpload = (file) => {
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      modal.error({ title: 'Invalid file type', content: 'Only JPG, PNG, WebP or GIF images are allowed.', centered: true });
      return Upload.LIST_IGNORE;
    }
    if (file.size > 10 * 1024 * 1024) {
      modal.error({ title: 'File too large', content: 'Image must be under 10 MB.', centered: true });
      return Upload.LIST_IGNORE;
    }
    return false; // prevent auto-upload
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'image_path',
      key: 'image',
      width: 80,
      render: (url) =>
        url ? (
          <Image
            src={`${IMAGE_BASE}${url}`}
            width={48}
            height={48}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            preview={{ src: `${IMAGE_BASE}${url}` }}
          />
        ) : (
          <InboxOutlined style={{ fontSize: 28, color: '#ccc' }} />
        ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Barcode',
      dataIndex: 'barcode',
      key: 'barcode',
      render: (barcode, record) => (
        <Space size={4}>
          <BarcodeOutlined />
          <Text code style={{ fontSize: 12 }}>{barcode}</Text>
          <Tooltip title="Copy barcode">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(barcode);
                message.success('Copied!');
              }}
            />
          </Tooltip>
          <Tooltip title="View barcode">
            <Button
              type="text"
              size="small"
              icon={<BarcodeOutlined />}
              onClick={() => setBarcodeModalItem(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'current_quantity',
      key: 'current_quantity',
      width: 80,
      sorter: (a, b) => a.current_quantity - b.current_quantity,
      render: (qty) => (
        <Tag color={qty > 0 ? 'green' : 'default'} style={{ fontWeight: 600 }}>
          {qty}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openAdjustModal(record)}>
            Adjust Stock
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          <Popconfirm
            title={`Delete "${record.name}"?`}
            description="This item will be soft-deleted and hidden."
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Button icon={<ReloadOutlined />} onClick={fetchItems}>
          Refresh
        </Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
          New Item
        </Button>
      </div>

      <Table
        dataSource={items}
        columns={columns}
        rowKey="item_id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 'max-content' }}
      />

      {/* Create / Edit Item Modal */}
      <Modal
        title={editingItem ? 'Edit Item' : 'New Item'}
        open={itemModalOpen}
        onCancel={() => setItemModalOpen(false)}
        onOk={handleItemSave}
        confirmLoading={savingItem}
        okText={editingItem ? 'Save Changes' : 'Create Item'}
        destroyOnClose
      >
        <Form form={itemForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="e.g. Foldable Table" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Optional description" />
          </Form.Item>
          <Form.Item label="Image">
            <Upload
              listType="picture"
              maxCount={1}
              fileList={imageFileList}
              beforeUpload={beforeUpload}
              onChange={({ fileList }) => setImageFileList(fileList)}
              onRemove={() => setImageFileList([])}
            >
              <Button icon={<UploadOutlined />}>Select Image</Button>
            </Upload>
            <Text type="secondary" style={{ fontSize: 12 }}>
              JPG / PNG / WebP / GIF, max 10 MB
            </Text>
            {editingItem?.image_path && imageFileList.length === 0 && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Current image: </Text>
                <Image
                  src={`${IMAGE_BASE}${editingItem.image_path}`}
                  height={60}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                />
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>

      {/* Adjust Stock Modal */}
      <Modal
        title={`Adjust Stock — ${adjustingItem?.name || ''}`}
        open={adjustModalOpen}
        onCancel={() => setAdjustModalOpen(false)}
        onOk={handleAdjustSave}
        confirmLoading={savingAdjust}
        okText="Confirm"
        destroyOnClose
      >
        {adjustingItem && (
          <Descriptions size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Current Qty">
              <strong>{adjustingItem.current_quantity}</strong>
            </Descriptions.Item>
          </Descriptions>
        )}
        {adjustingItem?.barcode && (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <BarcodeDisplay value={adjustingItem.barcode} width={1.5} height={55} fontSize={12} />
          </div>
        )}
        <Form
          form={adjustForm}
          layout="vertical"
          onValuesChange={(changed) => {
            if ('transaction_type' in changed) {
              setAdjustType(changed.transaction_type);
              setAdjustDirection('increase');
              adjustForm.setFieldsValue({ from_location_id: undefined, to_location_id: undefined });
            }
          }}
        >
          <Form.Item
            name="transaction_type"
            label="Type"
            rules={[{ required: true, message: 'Please select a type' }]}
          >
            <Select
              options={[
                { value: 'transfer', label: 'Transfer — move between locations (no qty change)' },
                { value: 'in', label: 'Stock In — receiving new items' },
                { value: 'out', label: 'Stock Out — consume / remove from system' },
                { value: 'adjust', label: 'Manual Adjust (correction)' },
              ]}
            />
          </Form.Item>
          {adjustType === 'adjust' && (
            <Form.Item label="Direction">
              <Radio.Group
                value={adjustDirection}
                onChange={(e) => setAdjustDirection(e.target.value)}
                buttonStyle="solid"
              >
                <Radio.Button value="increase">Increase</Radio.Button>
                <Radio.Button value="decrease">Decrease</Radio.Button>
              </Radio.Group>
            </Form.Item>
          )}
          <Form.Item
            name="quantity"
            label="Quantity"
            rules={[{ required: true, message: 'Quantity is required' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          {(adjustType === 'out' || adjustType === 'transfer') && (
            <Form.Item
              name="from_location_id"
              label="From Location"
              rules={
                adjustType === 'transfer'
                  ? [{ required: true, message: 'Origin location is required for transfers' }]
                  : []
              }
            >
              <Select
                allowClear={adjustType !== 'transfer'}
                placeholder={adjustType === 'transfer' ? 'Select origin location' : 'Main warehouse (leave blank)'}
                loading={locationsLoading}
                options={locations.map((l) => ({ value: l.location_id, label: l.name }))}
              />
            </Form.Item>
          )}
          {(adjustType === 'in' || adjustType === 'out' || adjustType === 'transfer') && (
            <Form.Item
              name="to_location_id"
              label={
                adjustType === 'transfer' ? 'To Location (destination)' :
                adjustType === 'out' ? 'To Location (destination)' :
                'To Location (optional)'
              }
              rules={
                adjustType === 'transfer'
                  ? [{ required: true, message: 'Destination location is required for transfers' }]
                  : adjustType === 'out'
                  ? [{ required: true, message: 'Destination location is required' }]
                  : []
              }
            >
              <Select
                allowClear={adjustType === 'in'}
                placeholder="Select destination location"
                loading={locationsLoading}
                options={locations.map((l) => ({ value: l.location_id, label: l.name }))}
              />
            </Form.Item>
          )}
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="e.g. Moved to Stage A for the event" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Barcode View Modal */}
      <BarcodeModal
        barcode={barcodeModalItem?.barcode}
        itemName={barcodeModalItem?.name}
        open={!!barcodeModalItem}
        onClose={() => setBarcodeModalItem(null)}
      />
    </>
  );
};

// ─── Transactions Tab ─────────────────────────────────────────────────────────

const TransactionsTab = () => {
  const [transactions, setTransactions] = useState(null); // null = not yet searched
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filterForm] = Form.useForm();

  const fetchItems = useCallback(async () => {
    try {
      setItemsLoading(true);
      const res = await inventoryService.getItems();
      setItems(Array.isArray(res) ? res : res.items || []);
    } catch (err) {
      message.error('Failed to load items list: ' + (err.message || 'Unknown error'));
    } finally {
      setItemsLoading(false);
    }
  }, []);

  const fetchTransactions = useCallback(async (f = {}) => {
    try {
      setLoading(true);
      const res = await inventoryService.getTransactions(f);
      setTransactions(Array.isArray(res) ? res : res.transactions || []);
    } catch (err) {
      message.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const applyFilters = async () => {
    const values = filterForm.getFieldsValue();
    const f = {};
    if (values.itemId) f.itemId = values.itemId;
    if (values.type) f.type = values.type;
    if (values.dateRange?.[0]) f.from = values.dateRange[0].format('YYYY-MM-DD');
    if (values.dateRange?.[1]) f.to = values.dateRange[1].format('YYYY-MM-DD');
    await fetchTransactions(f);
  };

  const clearFilters = () => {
    filterForm.resetFields();
    setTransactions(null);
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      defaultSortOrder: 'descend',
      render: (v) => v ? new Date(v).toLocaleString() : '—',
    },
    {
      title: 'Item',
      dataIndex: 'item_name',
      key: 'item_name',
      sorter: (a, b) => (a.item_name || '').localeCompare(b.item_name || ''),
    },
    {
      title: 'Barcode',
      dataIndex: 'barcode',
      key: 'barcode',
      render: (v) => v ? <Text code style={{ fontSize: 11 }}>{v}</Text> : '—',
    },
    {
      title: 'Type',
      dataIndex: 'transaction_type',
      key: 'transaction_type',
      width: 100,
      render: (t) => (
        <Tag color={typeTagColor[t] || 'default'} icon={typeIcon[t]}>
          {t?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 70,
      render: (qty, record) => (
        <Text strong style={{ color: record.transaction_type === 'out' ? '#f5222d' : record.transaction_type === 'in' ? '#52c41a' : '#1890ff' }}>
          {record.transaction_type === 'out' ? `-${qty}` : `+${qty}`}
        </Text>
      ),
    },
    {
      title: 'From',
      dataIndex: 'from_location_name',
      key: 'from_location_name',
      render: (v) =>
        v ? (
          <Tag icon={<EnvironmentOutlined />}>{v}</Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'To',
      dataIndex: 'to_location_name',
      key: 'to_location_name',
      render: (v) =>
        v ? (
          <Tag icon={<EnvironmentOutlined />} color="blue">{v}</Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Booking',
      key: 'booking',
      width: 160,
      render: (_, r) => {
        if (!r.reservation_id) return <Text type="secondary">—</Text>;
        const isDiscrepancy = r.transaction_type === 'adjust' && r.quantity < 0;
        return (
          <Space direction="vertical" size={0} style={{ fontSize: 11 }}>
            <Text code style={{ fontSize: 11 }}>#{r.reservation_id}</Text>
            {r.reserved_by_name && <Text type="secondary">{r.reserved_by_name}</Text>}
            {isDiscrepancy && <Tag color="red" style={{ fontSize: 10 }}>write-off</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'By',
      key: 'by',
      render: (_, r) => <Text style={{ fontSize: 12 }}>{r.scanner_name || r.submitted_by || <Text type="secondary">—</Text>}</Text>,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
  ];

  return (
    <>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Form form={filterForm} layout="inline">
          <Form.Item name="itemId" label="Item">
            <Select
              allowClear
              placeholder="All items"
              style={{ minWidth: 180 }}
              loading={itemsLoading}
              notFoundContent={
                items.length === 0 && !itemsLoading
                  ? <Button size="small" type="link" onClick={fetchItems}>Retry loading items</Button>
                  : undefined
              }
              options={items.map((i) => ({ value: i.item_id, label: i.name }))}
            />
          </Form.Item>
          <Form.Item name="type" label="Type">
            <Select
              allowClear
              placeholder="All types"
              style={{ minWidth: 140 }}
              options={[
                { value: 'in', label: 'In' },
                { value: 'out', label: 'Out' },
                { value: 'adjust', label: 'Adjust' },
                { value: 'transfer', label: 'Transfer' },
              ]}
            />
          </Form.Item>
          <Form.Item name="dateRange" label="Date Range">
            <RangePicker format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<FilterOutlined />} onClick={applyFilters}>
                Apply
              </Button>
              <Button icon={<ReloadOutlined />} onClick={clearFilters}>
                Clear
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Table
        dataSource={transactions ?? []}
        columns={columns}
        rowKey={(r, i) => r.transaction_id || i}
        loading={loading}
        pagination={{ pageSize: 25 }}
        size="small"
        scroll={{ x: 'max-content' }}
        locale={
          transactions === null
            ? { emptyText: 'Select a filter and click Apply to view transactions.' }
            : undefined
        }
      />
    </>
  );
};

// ─── Scanners Tab ─────────────────────────────────────────────────────────────

const ScannersTab = () => {
  const [scanners, setScanners] = useState([]);
  const [loading, setLoading] = useState(false);

  // Register modal
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [registerForm] = Form.useForm();
  const [registering, setRegistering] = useState(false);

  // Post-registration token display
  const [newScanner, setNewScanner] = useState(null);
  const [tokenModalOpen, setTokenModalOpen] = useState(false);

  // Revealed tokens in table (keyed by scanner_id)
  const [revealedTokens, setRevealedTokens] = useState({});

  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingScanner, setEditingScanner] = useState(null);
  const [editForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const fetchScanners = useCallback(async () => {
    try {
      setLoading(true);
      const res = await inventoryService.getScanners();
      setScanners(Array.isArray(res) ? res : res.scanners || []);
    } catch (err) {
      message.error('Failed to load scanners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScanners();
  }, [fetchScanners]);

  const openEditModal = (scanner) => {
    setEditingScanner(scanner);
    editForm.setFieldsValue({
      name: scanner.name,
      location: scanner.location,
      is_active: scanner.is_active,
    });
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    try {
      const values = await editForm.validateFields();
      setSaving(true);
      await inventoryService.updateScanner(editingScanner.scanner_id, values);
      message.success('Scanner updated');
      setEditModalOpen(false);
      fetchScanners();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Failed to update scanner');
    } finally {
      setSaving(false);
    }
  };

  const handleRegister = async () => {
    try {
      const values = await registerForm.validateFields();
      setRegistering(true);
      const res = await inventoryService.registerScanner(values);
      setRegisterModalOpen(false);
      registerForm.resetFields();
      fetchScanners();
      // Show token modal if token returned
      const created = res?.scanner || res;
      if (created?.token) {
        setNewScanner(created);
        setTokenModalOpen(true);
      } else {
        message.success('Scanner registered');
      }
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Failed to register scanner');
    } finally {
      setRegistering(false);
    }
  };

  const handleDeactivate = async (scanner) => {
    try {
      await inventoryService.deleteScanner(scanner.scanner_id);
      message.success(`Scanner "${scanner.name}" deactivated`);
      fetchScanners();
    } catch (err) {
      message.error('Failed to deactivate scanner');
    }
  };

  const handleRegenerateToken = async (scanner) => {
    try {
      const res = await inventoryService.regenerateScannerToken(scanner.scanner_id);
      fetchScanners();
      const token = res?.token;
      if (token) {
        setNewScanner({ ...scanner, token });
        setTokenModalOpen(true);
      } else {
        message.success('Token regenerated');
      }
    } catch (err) {
      message.error(err.message || 'Failed to regenerate token');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 110,
      render: (active) =>
        active ? (
          <Badge status="success" text="Active" />
        ) : (
          <Badge status="error" text="Inactive" />
        ),
    },
    {
      title: 'Last Seen',
      dataIndex: 'last_seen',
      key: 'last_seen',
      render: (v) => (v ? new Date(v).toLocaleString() : <Text type="secondary">Never</Text>),
    },
    {
      title: 'Token',
      dataIndex: 'token',
      key: 'token',
      render: (token, record) => {
        if (!token) return <Text type="secondary">—</Text>;
        const revealed = revealedTokens[record.scanner_id];
        return (
          <Space size={4}>
            <Text code style={{ fontSize: 11 }}>
              {revealed ? token : `${token.slice(0, 6)}••••••••${token.slice(-4)}`}
            </Text>
            <Tooltip title={revealed ? 'Hide token' : 'Reveal token'}>
              <Button
                type="text"
                size="small"
                icon={revealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                onClick={() =>
                  setRevealedTokens((prev) => ({
                    ...prev,
                    [record.scanner_id]: !prev[record.scanner_id],
                  }))
                }
              />
            </Tooltip>
            <Tooltip title="Copy token">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => {
                  navigator.clipboard.writeText(token);
                  message.success('Token copied!');
                }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)}>
            Edit
          </Button>
          <Popconfirm
            title={`Deactivate "${record.name}"?`}
            description="The scanner token will stop working immediately."
            onConfirm={() => handleDeactivate(record)}
            okText="Deactivate"
            okButtonProps={{ danger: true }}
            disabled={!record.is_active}
          >
            <Button
              size="small"
              danger
              icon={<StopOutlined />}
              disabled={!record.is_active}
            >
              Deactivate
            </Button>
          </Popconfirm>
          <Popconfirm
            title={`Regenerate token for "${record.name}"?`}
            description="The current token stops working immediately."
            onConfirm={() => handleRegenerateToken(record)}
            okText="Regenerate"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Regenerate token">
              <Button size="small" icon={<SyncOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Button icon={<ReloadOutlined />} onClick={fetchScanners}>
          Refresh
        </Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setRegisterModalOpen(true)}>
          Register Scanner
        </Button>
      </div>

      <Table
        dataSource={scanners}
        columns={columns}
        rowKey="scanner_id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 'max-content' }}
      />

      {/* Post-registration token modal */}
      <Modal
        title={
          <Space>
            <KeyOutlined style={{ color: '#52c41a' }} />
            Scanner Registered
          </Space>
        }
        open={tokenModalOpen}
        onCancel={() => setTokenModalOpen(false)}
        footer={[
          <Button
            key="copy"
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => {
              navigator.clipboard.writeText(newScanner?.token || '');
              message.success('Token copied!');
            }}
          >
            Copy Token
          </Button>,
          <Button key="close" onClick={() => setTokenModalOpen(false)}>Close</Button>,
        ]}
        centered
        width={480}
      >
        <Alert
          type="warning"
          showIcon
          message="Copy this token now"
          description="The token is always retrievable from the Scanners list, but copy it now to configure the scanner device."
          style={{ marginBottom: 16 }}
        />
        {newScanner && (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Name">{newScanner.name}</Descriptions.Item>
            {newScanner.location && (
              <Descriptions.Item label="Location">{newScanner.location}</Descriptions.Item>
            )}
            <Descriptions.Item label="Token">
              <Text code style={{ fontSize: 12, wordBreak: 'break-all' }}>
                {newScanner.token}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Register Scanner Modal */}
      <Modal
        title="Register Scanner"
        open={registerModalOpen}
        onCancel={() => { setRegisterModalOpen(false); registerForm.resetFields(); }}
        onOk={handleRegister}
        confirmLoading={registering}
        okText="Register"
        destroyOnClose
      >
        <Form form={registerForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Scanner Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="e.g. Warehouse Scanner 1" />
          </Form.Item>
          <Form.Item name="location" label="Location">
            <Input placeholder="e.g. Main Store" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Scanner Modal */}
      <Modal
        title="Edit Scanner"
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={handleEditSave}
        confirmLoading={saving}
        okText="Save Changes"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Scanner Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="location" label="Location">
            <Input placeholder="e.g. Stage B" />
          </Form.Item>
          <Form.Item name="is_active" label="Status">
            <Select
              options={[
                { value: 1, label: 'Active' },
                { value: 0, label: 'Inactive' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ─── Cart Booking Modal ───────────────────────────────────────────────────────

const CartBookingModal = ({ cart, open, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const bookingDates = Form.useWatch('dateRange', form);
  const winStart = bookingDates?.[0] || null;
  const winEnd = bookingDates?.[1] || null;
  const [saving, setSaving] = useState(false);
  const { locations, loading: locationsLoading } = useReservationLocations(open);
  const [itemAvail, setItemAvail] = useState({});
  const [quantities, setQuantities] = useState({}); // { [item_id]: number } — total to book
  const [splits, setSplits] = useState({}); // { [item_id]: [{location_id, quantity, start_date, end_date}] }
  const [localCart, setLocalCart] = useState([]);
  const [partialResult, setPartialResult] = useState(null);
  const [monthCache, setMonthCache] = useState({});
  const loadingMonths = useRef(new Set());

  useEffect(() => {
    if (!open) return;
    setPartialResult(null);
    setItemAvail({});
    setMonthCache({});
    loadingMonths.current.clear();
    setLocalCart([...cart]);
    const initSplits = {};
    const initQty = {};
    cart.forEach(item => {
      initQty[item.item_id] = 1;
      initSplits[item.item_id] = [{ location_id: undefined, quantity: 1 }];
    });
    setQuantities(initQty);
    setSplits(initSplits);
    form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cart]);

  const fetchMonthAvail = useCallback((year, month) => {
    localCart.forEach(item => {
      const key = `${item.item_id}-${year}-${month}`;
      if (loadingMonths.current.has(key)) return;
      setMonthCache(prev => {
        if (prev[key]) return prev;
        loadingMonths.current.add(key);
        inventoryService.getMonthAvailability(item.item_id, year, month)
          .then(res => setMonthCache(p => ({ ...p, [key]: { availability: res.availability, total: res.current_quantity } })))
          .catch(() => {})
          .finally(() => loadingMonths.current.delete(key));
        return prev;
      });
    });
  }, [localCart]);

  const cellRender = useCallback((date, info) => {
    if (info.type !== 'date' || localCart.length === 0) return info.originNode;
    const year = date.year();
    const month = date.month() + 1;
    const dateStr = date.format('YYYY-MM-DD');

    let minRatio = null;
    let minAvail = null;
    const details = [];
    let anyCached = false;

    for (const item of localCart) {
      const key = `${item.item_id}-${year}-${month}`;
      const cached = monthCache[key];
      if (!cached) continue;
      anyCached = true;
      const dayData = cached.availability[dateStr];
      const avail = dayData?.available ?? cached.total;
      const ratio = cached.total > 0 ? avail / cached.total : 0;
      details.push(`${item.name}: ${avail} available`);
      if (minRatio === null || ratio < minRatio) minRatio = ratio;
      if (minAvail === null || avail < minAvail) minAvail = avail;
    }

    if (!anyCached || minRatio === null) return info.originNode;
    const color = minAvail <= 0 ? '#f5222d' : minRatio < 0.5 ? '#fa8c16' : '#52c41a';

    return (
      <Tooltip title={details.join(' · ')} mouseEnterDelay={0.1}>
        <div style={{ position: 'relative' }}>
          {info.originNode}
          <span style={{
            position: 'absolute', bottom: 1, left: '50%', transform: 'translateX(-50%)',
            width: 5, height: 5, borderRadius: '50%', backgroundColor: color, display: 'block',
          }} />
        </div>
      </Tooltip>
    );
  }, [localCart, monthCache]);

  // When the booking window changes, snap each location split into the new window
  // (default rows that have no dates yet, and clamp any that now fall outside).
  const syncSplitsToWindow = (dates) => {
    if (!dates?.[0] || !dates?.[1]) return;
    const ws = dates[0];
    const we = dates[1];
    const clamp = (row) => {
      let s = row.start_date ? dayjs(row.start_date) : ws;
      let e = row.end_date ? dayjs(row.end_date) : we;
      if (s.isBefore(ws, 'day')) s = ws;
      if (e.isAfter(we, 'day')) e = we;
      if (s.isAfter(e, 'day')) { s = ws; e = we; }
      return { ...row, start_date: s.format('YYYY-MM-DD'), end_date: e.format('YYYY-MM-DD') };
    };
    setSplits(prev => {
      const next = {};
      Object.keys(prev).forEach(itemId => { next[itemId] = prev[itemId].map(clamp); });
      return next;
    });
  };

  const checkAvailability = async (dates) => {
    syncSplitsToWindow(dates);
    if (!dates?.[0] || !dates?.[1]) { setItemAvail({}); return; }
    const start = dates[0].format('YYYY-MM-DD');
    const end = dates[1].format('YYYY-MM-DD');
    setItemAvail(Object.fromEntries(localCart.map(i => [i.item_id, { checking: true }])));
    const results = await Promise.allSettled(
      localCart.map(item =>
        inventoryService.getAvailability(item.item_id, start, end)
          .then(res => ({ item_id: item.item_id, qty: res.available_quantity ?? 0 }))
      )
    );
    const avail = {};
    results.forEach((r, idx) => {
      const itemId = localCart[idx]?.item_id;
      if (r.status === 'fulfilled') avail[r.value.item_id] = { qty: r.value.qty, checking: false };
      else if (itemId) avail[itemId] = { qty: null, checking: false, error: true };
    });
    setItemAvail(avail);
  };

  const setItemSplits = (itemId, next) => setSplits(prev => ({ ...prev, [itemId]: next }));

  // Setting the item total; if there's a single split row, keep it in sync automatically
  const setItemQuantity = (itemId, qty) => {
    const q = qty || 1;
    setQuantities(prev => ({ ...prev, [itemId]: q }));
    setSplits(prev => {
      const rows = prev[itemId] || [];
      if (rows.length === 1) return { ...prev, [itemId]: [{ ...rows[0], quantity: q }] };
      return prev;
    });
  };

  const handleOk = async () => {
    if (localCart.length === 0) { message.error('Add at least one item first'); return; }
    try {
      const values = await form.validateFields();

      // Each item must have a complete, non-zero location split
      const incomplete = localCart.filter(item => !splitsComplete(splits[item.item_id] || []));
      if (incomplete.length > 0) {
        message.error(`Assign a location, dates and quantity for: ${incomplete.map(i => i.name).join(', ')}`);
        return;
      }

      // Location split must total the quantity requested for that item
      const unbalanced = localCart.filter(item =>
        splitsTotal(splits[item.item_id] || []) !== (quantities[item.item_id] || 0)
      );
      if (unbalanced.length > 0) {
        message.error(`Location split must total the quantity for: ${unbalanced.map(i => i.name).join(', ')}`);
        return;
      }

      const overLimit = localCart.filter(item => {
        const avail = itemAvail[item.item_id];
        const total = quantities[item.item_id] || 0;
        return avail?.qty != null && total > avail.qty;
      });
      if (overLimit.length > 0) {
        message.error(`Some quantities exceed availability: ${overLimit.map(i => i.name).join(', ')}`);
        return;
      }

      setSaving(true);
      const result = await inventoryService.createBooking({
        start_date: values.dateRange[0].format('YYYY-MM-DD'),
        end_date: values.dateRange[1].format('YYYY-MM-DD'),
        notes: values.notes || '',
        items: localCart.map(item => ({
          item_id: item.item_id,
          quantity: quantities[item.item_id] || 0,
          locations: splitsToPayload(splits[item.item_id] || []),
        })),
      });
      if (result.partial) {
        setPartialResult(result);
      } else {
        message.success(`Booking #${result.booking_id} created`);
        form.resetFields();
        onSuccess(result);
      }
    } catch (err) {
      if (err?.errorFields) return;
      if (err.status === 409) {
        setPartialResult({ type: '409', rejected: err.rejected });
      } else {
        message.error(err.message || 'Failed to create booking');
      }
    } finally {
      setSaving(false);
    }
  };

  const visibleCart = partialResult?.rejected
    ? localCart.filter(i => partialResult.rejected.some(r => r.item_id === i.item_id))
    : localCart;

  return (
    <Modal
      title={`Create Booking (${localCart.length} item${localCart.length !== 1 ? 's' : ''})`}
      open={open}
      onCancel={() => { form.resetFields(); setPartialResult(null); onClose(); }}
      onOk={partialResult && !partialResult.type ? () => onSuccess(partialResult) : handleOk}
      okText={partialResult && !partialResult.type ? 'Done' : 'Confirm Booking'}
      confirmLoading={saving}
      destroyOnClose
      width={640}
    >
      {partialResult?.created?.length > 0 && (
        <Alert
          type="success" showIcon
          message={`Booking #${partialResult.booking_id} — ${partialResult.created.length} item(s) reserved`}
          description={partialResult.created.map(c => `${c.name} ×${c.quantity}`).join(', ')}
          style={{ marginBottom: 12 }}
        />
      )}
      {partialResult?.rejected?.length > 0 && (
        <Alert
          type={partialResult.type === '409' ? 'error' : 'warning'} showIcon
          message={
            partialResult.type === '409'
              ? 'None of the items could be reserved'
              : `${partialResult.rejected.length} item(s) couldn't be reserved — adjust and retry`
          }
          description={
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {partialResult.rejected.map(r => (
                <li key={r.item_id}>{r.name}: requested {r.requested}, only {r.available} available</li>
              ))}
            </ul>
          }
          style={{ marginBottom: 12 }}
        />
      )}

      <Form form={form} layout="vertical">
        <Form.Item name="dateRange" label="Booking Dates" rules={[{ required: true, message: 'Please select dates' }]}>
          <RangePicker
            format="YYYY-MM-DD"
            style={{ width: '100%' }}
            disabledDate={(d) => d && d.isBefore(new Date(), 'day')}
            onChange={checkAvailability}
            cellRender={cellRender}
            onOpenChange={(isOpen) => {
              if (!isOpen) return;
              const now = new Date();
              fetchMonthAvail(now.getFullYear(), now.getMonth() + 1);
              const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
              fetchMonthAvail(next.getFullYear(), next.getMonth() + 1);
            }}
            onPanelChange={(dates) => {
              if (dates?.[0]) fetchMonthAvail(dates[0].year(), dates[0].month() + 1);
              if (dates?.[1]) fetchMonthAvail(dates[1].year(), dates[1].month() + 1);
            }}
          />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} placeholder="e.g. Weekend festival stage setup" />
        </Form.Item>
      </Form>

      {/* Cart items with per-item location splits */}
      <Text strong style={{ display: 'block', marginBottom: 8 }}>Items & locations</Text>
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {visibleCart.map(item => {
          const avail = itemAvail[item.item_id];
          const itemSplits = splits[item.item_id] || [];
          const qty = quantities[item.item_id] || 1;
          const allocated = splitsTotal(itemSplits);
          const exceeds = avail?.qty != null && qty > avail.qty;
          const multiSplit = itemSplits.length > 1;
          return (
            <div key={item.item_id} style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <Text strong>{item.name}</Text>
                  {avail?.checking && <Tag style={{ marginLeft: 6, fontSize: 11 }}>checking…</Tag>}
                  {!avail?.checking && avail?.qty != null && (
                    <Tag color={avail.qty <= 0 ? 'red' : avail.qty < 3 ? 'orange' : 'green'} style={{ marginLeft: 6, fontSize: 11 }}>
                      {avail.qty} avail
                    </Tag>
                  )}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>Qty</Text>
                <InputNumber
                  min={1}
                  value={qty}
                  onChange={(v) => setItemQuantity(item.item_id, v)}
                  style={{ width: 80 }}
                  status={exceeds ? 'error' : undefined}
                />
                {!partialResult && (
                  <Button
                    size="small" type="text" danger
                    onClick={() => setLocalCart(prev => prev.filter(i => i.item_id !== item.item_id))}
                  >
                    Remove
                  </Button>
                )}
              </div>
              {exceeds && (
                <Text type="danger" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                  Quantity {qty} exceeds {avail.qty} available for this window — reduce to continue.
                </Text>
              )}
              <LocationSplitEditor
                splits={itemSplits}
                onChange={(next) => setItemSplits(item.item_id, next)}
                locations={locations}
                locationsLoading={locationsLoading}
                windowStart={winStart}
                windowEnd={winEnd}
                targetTotal={qty}
              />
              {multiSplit && allocated !== qty && (
                <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                  Allocated {allocated} of {qty} — distribute the full quantity across locations.
                </Text>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

// ─── Edit Reservation Modal ───────────────────────────────────────────────────

const EditReservationModal = ({ reservation, open, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const watchedQty = Form.useWatch('quantity', form);
  const [activeTabKey, setActiveTabKey] = useState('edit');
  const [saving, setSaving] = useState(false);
  const [windowAvail, setWindowAvail] = useState(null);
  const [checkingAvail, setCheckingAvail] = useState(false);
  const [conflictMsg, setConflictMsg] = useState(null);
  const [historyRows, setHistoryRows] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const { locations, loading: locationsLoading } = useReservationLocations(open);

  // Location split
  const [splits, setSplits] = useState([]);
  const [originalSplits, setOriginalSplits] = useState([]);

  const isOut = reservation?.status === 'out';
  // Split must total the (editable for active) quantity, or the fixed quantity when dispatched
  const targetTotal = isOut ? (reservation?.quantity ?? 0) : (watchedQty ?? reservation?.quantity ?? 0);

  useEffect(() => {
    if (!open || !reservation) return;
    setConflictMsg(null);
    setHistoryRows(null);
    setActiveTabKey('edit');
    form.setFieldsValue({ quantity: reservation.quantity, notes: reservation.notes || '' });

    const initSplits = splitsFromLine(reservation);
    setSplits(initSplits);
    setOriginalSplits(initSplits);

    const resItemId = lineItemId(reservation);
    if (reservation.start_date && reservation.end_date && resItemId != null) {
      setCheckingAvail(true);
      const start = dayjs(reservation.start_date).format('YYYY-MM-DD');
      const end = dayjs(reservation.end_date).format('YYYY-MM-DD');
      inventoryService.getAvailability(resItemId, start, end, reservation.reservation_id)
        .then(res => setWindowAvail(res.available_quantity ?? 0))
        .catch(() => {})
        .finally(() => setCheckingAvail(false));
    } else {
      setWindowAvail(null);
    }
  }, [open, reservation, form]);

  const loadHistory = useCallback(async () => {
    if (!reservation || historyRows !== null) return;
    setHistoryLoading(true);
    try {
      const res = await inventoryService.getReservationChanges(reservation.reservation_id);
      setHistoryRows(res.changes || []);
    } catch {
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [reservation, historyRows]);

  const splitsChanged = JSON.stringify(splits) !== JSON.stringify(originalSplits);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setConflictMsg(null);

      // Validate the location split sums to the quantity
      if (!splitsComplete(splits)) {
        message.error('Each location row needs a location and quantity');
        return;
      }
      const total = splitsTotal(splits);
      const mustTotal = isOut ? reservation.quantity : values.quantity;
      if (total !== mustTotal) {
        message.error(`Location split totals ${total} but must total ${mustTotal}`);
        return;
      }

      setSaving(true);

      if (!isOut) {
        // Update quantity / notes first so the new quantity is the split target server-side
        const payload = {};
        if (values.quantity !== reservation.quantity) payload.quantity = values.quantity;
        if ((values.notes || '') !== (reservation.notes || '')) payload.notes = values.notes || '';
        if (Object.keys(payload).length > 0) {
          await inventoryService.updateReservation(reservation.reservation_id, payload);
        }
        if (splitsChanged || ('quantity' in payload)) {
          await inventoryService.updateReservationLocations(
            reservation.reservation_id,
            splitsToPayload(splits)
          );
        }
        if (Object.keys(payload).length === 0 && !splitsChanged) {
          message.info('No changes to save');
          setSaving(false);
          return;
        }
      } else {
        if (!splitsChanged) { message.info('No changes to save'); setSaving(false); return; }
        await inventoryService.updateReservationLocations(
          reservation.reservation_id,
          splits.map(s => ({ location_id: s.location_id, quantity: s.quantity }))
        );
      }

      message.success('Updated');
      onSuccess();
    } catch (err) {
      if (err?.errorFields) return;
      if (err.status === 409 && err.available_quantity != null) {
        setConflictMsg(`Only ${err.available_quantity} unit(s) available for those dates`);
      } else {
        message.error(err.message || 'Failed to update');
      }
    } finally {
      setSaving(false);
    }
  };

  const tabItems = [
    {
      key: 'edit',
      label: isOut ? 'Locations' : 'Edit',
      children: (
        <>
          {conflictMsg && (
            <Alert type="error" showIcon message={conflictMsg} style={{ marginBottom: 12 }} closable onClose={() => setConflictMsg(null)} />
          )}
          {isOut && (
            <Alert
              type="info" showIcon
              message="This item has been dispatched — you can re-plan where its quantity sits, but not change the amount."
              style={{ marginBottom: 12 }}
            />
          )}
          <Form form={form} layout="vertical">
            {!isOut && (
              <>
                <Form.Item
                  name="quantity"
                  label={checkingAvail ? 'Quantity (checking…)' : windowAvail !== null ? `Quantity (${windowAvail} available in booking window)` : 'Quantity'}
                  rules={[
                    { required: true, message: 'Quantity is required' },
                    {
                      validator: (_, value) => {
                        if (windowAvail !== null && value > windowAvail)
                          return Promise.reject(`Exceeds available stock (${windowAvail})`);
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber min={1} style={{ width: '100%' }} disabled={checkingAvail} />
                </Form.Item>
                <Form.Item name="notes" label="Notes">
                  <Input.TextArea rows={2} />
                </Form.Item>
              </>
            )}
            <Form.Item label="Location split" style={{ marginBottom: 0 }}>
              <LocationSplitEditor
                splits={splits}
                onChange={setSplits}
                locations={locations}
                locationsLoading={locationsLoading}
                targetTotal={targetTotal}
                windowStart={reservation?.start_date}
                windowEnd={reservation?.end_date}
              />
            </Form.Item>
          </Form>
        </>
      ),
    },
    {
      key: 'history',
      label: <span><ClockCircleOutlined style={{ marginRight: 4 }} />History</span>,
      children: historyLoading
        ? <div style={{ padding: '16px 0', textAlign: 'center' }}><Text type="secondary">Loading…</Text></div>
        : !historyRows?.length
        ? <div style={{ padding: '12px 0' }}><Text type="secondary">No edits recorded.</Text></div>
        : (
          <Timeline style={{ marginTop: 12 }} items={historyRows.map(c => ({
            color: 'blue',
            children: (
              <div>
                <Text strong style={{ textTransform: 'capitalize' }}>{c.field.replace(/_/g, ' ')}</Text>
                {' '}
                <Text type="secondary" style={{ fontSize: 12 }}>{c.old_value} → {c.new_value}</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {c.changed_by_name} · {c.changed_at ? new Date(c.changed_at).toLocaleString() : '—'}
                  </Text>
                </div>
              </div>
            ),
          }))} />
        ),
    },
  ];

  return (
    <Modal
      title={`Edit — ${reservation?.item_name || lineItemName(reservation) || `Line #${reservation?.reservation_id}`}`}
      open={open}
      onCancel={() => { form.resetFields(); setConflictMsg(null); onClose(); }}
      onOk={activeTabKey === 'edit' ? handleOk : undefined}
      footer={activeTabKey === 'edit' ? undefined : null}
      confirmLoading={saving}
      okText="Save Changes"
      destroyOnClose
      width={480}
    >
      <Tabs activeKey={activeTabKey} onChange={(k) => { setActiveTabKey(k); if (k === 'history') loadHistory(); }} items={tabItems} size="small" />
    </Modal>
  );
};

// ─── Edit Booking Modal (booking-level: dates / location / notes) ─────────────

const EditBookingModal = ({ booking, open, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [failedItems, setFailedItems] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [lineItems, setLineItems] = useState([]);

  // Calendar availability dots (across all the booking's products)
  const [monthCache, setMonthCache] = useState({});
  const loadingMonths = useRef(new Set());

  const fetchMonthAvail = useCallback((year, month) => {
    lineItems.forEach(line => {
      const key = `${line.reservation_id}-${year}-${month}`;
      if (loadingMonths.current.has(key)) return;
      setMonthCache(prev => {
        if (prev[key]) return prev;
        loadingMonths.current.add(key);
        inventoryService.getMonthAvailability(lineItemId(line), year, month, line.reservation_id)
          .then(res => setMonthCache(p => ({ ...p, [key]: { availability: res.availability, total: res.current_quantity } })))
          .catch(() => {})
          .finally(() => loadingMonths.current.delete(key));
        return prev;
      });
    });
  }, [lineItems]);

  const cellRender = useCallback((date, info) => {
    if (info.type !== 'date' || lineItems.length === 0) return info.originNode;
    const year = date.year();
    const month = date.month() + 1;
    const dateStr = date.format('YYYY-MM-DD');

    let minRatio = null;
    let minAvail = null;
    const details = [];
    let anyCached = false;

    for (const line of lineItems) {
      const key = `${line.reservation_id}-${year}-${month}`;
      const cached = monthCache[key];
      if (!cached) continue;
      anyCached = true;
      const dayData = cached.availability[dateStr];
      const avail = dayData?.available ?? cached.total;
      const ratio = cached.total > 0 ? avail / cached.total : 0;
      details.push(`${lineItemName(line)}: ${avail} available`);
      if (minRatio === null || ratio < minRatio) minRatio = ratio;
      if (minAvail === null || avail < minAvail) minAvail = avail;
    }

    if (!anyCached || minRatio === null) return info.originNode;
    const color = minAvail <= 0 ? '#f5222d' : minRatio < 0.5 ? '#fa8c16' : '#52c41a';

    return (
      <Tooltip title={details.join(' · ')} mouseEnterDelay={0.1}>
        <div style={{ position: 'relative' }}>
          {info.originNode}
          <span style={{
            position: 'absolute', bottom: 1, left: '50%', transform: 'translateX(-50%)',
            width: 5, height: 5, borderRadius: '50%', backgroundColor: color, display: 'block',
          }} />
        </div>
      </Tooltip>
    );
  }, [lineItems, monthCache]);

  useEffect(() => {
    if (!open || !booking) return;
    setFailedItems(null);
    setLineItems([]);
    setMonthCache({});
    loadingMonths.current.clear();
    form.setFieldsValue({
      dateRange: [dayjs(booking.start_date), dayjs(booking.end_date)],
      notes: booking.notes || '',
    });

    setLoadingDetail(true);
    inventoryService.getBooking(booking.booking_id)
      .then(res => {
        const detail = res.booking || res;
        setLineItems(detail.items || []);
        form.setFieldsValue({
          dateRange: [dayjs(detail.start_date), dayjs(detail.end_date)],
          notes: detail.notes || '',
        });
      })
      .catch(() => {})
      .finally(() => setLoadingDetail(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, booking, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setFailedItems(null);

      const newStart = values.dateRange[0].format('YYYY-MM-DD');
      const newEnd = values.dateRange[1].format('YYYY-MM-DD');
      const oldStart = dayjs(booking.start_date).format('YYYY-MM-DD');
      const oldEnd = dayjs(booking.end_date).format('YYYY-MM-DD');

      const payload = {};
      if (newStart !== oldStart) payload.start_date = newStart;
      if (newEnd !== oldEnd) payload.end_date = newEnd;
      if ((values.notes || '') !== (booking.notes || '')) payload.notes = values.notes || '';

      if (Object.keys(payload).length === 0) { message.info('No changes to save'); return; }

      setSaving(true);
      await inventoryService.updateBooking(booking.booking_id, payload);
      message.success('Booking updated');
      onSuccess();
    } catch (err) {
      if (err?.errorFields) return;
      if (err.status === 409 && err.failed) {
        setFailedItems(err.failed);
      } else {
        message.error(err.message || 'Failed to update booking');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Edit Booking #${booking?.booking_id}`}
      open={open}
      onCancel={() => { form.resetFields(); setFailedItems(null); onClose(); }}
      onOk={handleOk}
      confirmLoading={saving || loadingDetail}
      okText="Save Changes"
      destroyOnClose
    >
      <Alert
        type="info" showIcon
        message="Dates and notes are shared by the whole booking. Edit each item's quantity and locations from its row in the booking detail."
        style={{ marginBottom: 16 }}
      />
      {failedItems?.length > 0 && (
        <Alert
          type="error" showIcon
          message="These items don't fit in the new window"
          description={
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {failedItems.map((f, i) => (
                <li key={i}>{f.name || f.item_id}: {f.available} available</li>
              ))}
            </ul>
          }
          style={{ marginBottom: 16 }}
          closable onClose={() => setFailedItems(null)}
        />
      )}
      <Form form={form} layout="vertical">
        <Form.Item
          name="dateRange"
          label="Date Range"
          rules={[{ required: true, message: 'Please select a date range' }]}
          extra={lineItems.length > 1 ? `Changing dates re-checks all ${lineItems.length} products.` : undefined}
        >
          <RangePicker
            format="YYYY-MM-DD"
            style={{ width: '100%' }}
            disabledDate={(d) => d && d.isBefore(new Date(), 'day')}
            cellRender={cellRender}
            onOpenChange={(isOpen) => {
              if (!isOpen) return;
              const now = new Date();
              fetchMonthAvail(now.getFullYear(), now.getMonth() + 1);
              const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
              fetchMonthAvail(next.getFullYear(), next.getMonth() + 1);
            }}
            onPanelChange={(dates) => {
              if (dates?.[0]) fetchMonthAvail(dates[0].year(), dates[0].month() + 1);
              if (dates?.[1]) fetchMonthAvail(dates[1].year(), dates[1].month() + 1);
            }}
          />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ─── Booking Lines Table (expandable booking detail) ──────────────────────────

const BookingLinesTable = ({ bookingId, canEdit = false, canActOnLifecycle = false, useMovements = false, onBookingChange }) => {
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editLineTarget, setEditLineTarget] = useState(null);

  // Dispatch modal
  const [dispatchTarget, setDispatchTarget] = useState(null);
  const [dispatchForm] = Form.useForm();
  const [dispatching, setDispatching] = useState(false);

  // Return modal
  const [returnTarget, setReturnTarget] = useState(null);
  const [returnForm] = Form.useForm();
  const [returning, setReturning] = useState(false);
  const [returnQty, setReturnQty] = useState(null);

  const fetchBooking = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    try {
      const res = await inventoryService.getBooking(bookingId);
      setBooking(res.booking || res);
    } catch {
      setBooking(null);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  useInventoryEvents({
    onBooking: (payload) => { if (payload.booking_id === bookingId) fetchBooking(); },
  });

  const handleCancelLine = async (line) => {
    try {
      await inventoryService.cancelReservation(line.reservation_id);
      message.success('Item cancelled');
      fetchBooking();
      onBookingChange?.();
    } catch (err) {
      message.error(err.message || 'Failed to cancel item');
    }
  };

  const handleDispatch = async () => {
    try {
      const values = await dispatchForm.validateFields();
      setDispatching(true);
      await inventoryService.dispatch(dispatchTarget.reservation_id, {
        quantity: values.quantity || undefined,
        notes: values.notes || undefined,
      });
      message.success('Items dispatched');
      dispatchForm.resetFields();
      setDispatchTarget(null);
      fetchBooking();
      onBookingChange?.();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Dispatch failed');
    } finally {
      setDispatching(false);
    }
  };

  const handleReturn = async () => {
    try {
      const values = await returnForm.validateFields();
      setReturning(true);
      await inventoryService.returnReservation(returnTarget.reservation_id, {
        quantity: values.quantity || undefined,
        discrepancy_reason: values.discrepancy_reason || undefined,
        discrepancy_note: values.discrepancy_note || undefined,
        notes: values.notes || undefined,
      });
      message.success('Return recorded');
      returnForm.resetFields();
      setReturnTarget(null);
      setReturnQty(null);
      fetchBooking();
      onBookingChange?.();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Return failed');
    } finally {
      setReturning(false);
    }
  };

  if (loading) return <div style={{ padding: '16px 24px' }}><Text type="secondary">Loading…</Text></div>;
  if (!booking) return <div style={{ padding: '16px 24px' }}><Text type="secondary">Could not load booking detail.</Text></div>;

  const lineColumns = [
    {
      title: 'Item',
      key: 'name',
      render: (_, line) => <Text strong>{lineItemName(line)}</Text>,
    },
    {
      title: 'Quantities',
      key: 'quantities',
      width: 160,
      render: (_, r) => (
        <Space direction="vertical" size={0} style={{ fontSize: 12 }}>
          <Text>Booked: <Text strong>{r.quantity}</Text></Text>
          {r.dispatched_quantity > 0 && <Text>Out: <Text strong style={{ color: '#fa8c16' }}>{r.dispatched_quantity}</Text></Text>}
          {r.returned_quantity > 0 && <Text>Back: <Text strong style={{ color: '#52c41a' }}>{r.returned_quantity}</Text></Text>}
          {r.discrepancy_quantity > 0 && (
            <Tooltip title={`${r.discrepancy_reason}${r.discrepancy_note ? ': ' + r.discrepancy_note : ''}`}>
              <Text style={{ color: '#f5222d' }}>Lost: <Text strong style={{ color: '#f5222d' }}>{r.discrepancy_quantity}</Text></Text>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Locations',
      key: 'locations',
      width: 220,
      render: (_, line) => renderLocationTags(line.locations),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: renderBookingStatus,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 260,
      render: (_, line) => {
        const isActive = line.status === 'active';
        const isOut = line.status === 'out';
        const openLineEditor = () => setEditLineTarget({ ...line, start_date: booking.start_date, end_date: booking.end_date, item_name: lineItemName(line) });
        const actions = [];
        if (canEdit && isActive) {
          actions.push(
            <Button key="edit" size="small" icon={<EditOutlined />} onClick={openLineEditor}>Edit</Button>
          );
          actions.push(
            <Popconfirm key="cancel" title="Cancel this item from the booking?" onConfirm={() => handleCancelLine(line)} okText="Cancel it" okButtonProps={{ danger: true }}>
              <Button size="small" danger>Cancel</Button>
            </Popconfirm>
          );
        }
        // Location coordination remains available after dispatch (planning only)
        if (canEdit && isOut) {
          actions.push(
            <Button key="locations" size="small" icon={<EnvironmentOutlined />} onClick={openLineEditor}>Locations</Button>
          );
        }
        if (canActOnLifecycle && isActive) {
          actions.push(
            <Button key="dispatch" size="small" type="primary" onClick={() => { dispatchForm.resetFields(); setDispatchTarget(line); }}>
              Dispatch
            </Button>
          );
        }
        if (canActOnLifecycle && isOut) {
          actions.push(
            <Button key="return" size="small" onClick={() => { returnForm.resetFields(); setReturnQty(null); setReturnTarget(line); }}>
              Return
            </Button>
          );
        }
        return actions.length ? <Space wrap>{actions}</Space> : null;
      },
    },
  ];

  return (
    <div style={{ padding: '4px 0 12px 24px' }}>
      <Table
        dataSource={booking.items || []}
        columns={lineColumns}
        rowKey="reservation_id"
        pagination={false}
        size="small"
        expandable={{
          expandedRowRender: (line) => (
            <div style={{ paddingLeft: 16 }}>
              <BookingTimeline reservationId={line.reservation_id} useMovements={useMovements} />
            </div>
          ),
          rowExpandable: () => true,
        }}
      />

      <EditReservationModal
        reservation={editLineTarget}
        open={!!editLineTarget}
        onClose={() => setEditLineTarget(null)}
        onSuccess={() => { setEditLineTarget(null); fetchBooking(); onBookingChange?.(); }}
      />

      <Modal
        title={`Dispatch — ${dispatchTarget ? lineItemName(dispatchTarget) : ''}`}
        open={!!dispatchTarget}
        onCancel={() => { dispatchForm.resetFields(); setDispatchTarget(null); }}
        onOk={handleDispatch}
        confirmLoading={dispatching}
        okText="Confirm Dispatch"
        destroyOnClose
      >
        {dispatchTarget && (
          <Descriptions size="small" style={{ marginBottom: 16 }} bordered>
            <Descriptions.Item label="Booked">{dispatchTarget.quantity}</Descriptions.Item>
            <Descriptions.Item label="Dates">{fmtDate(booking.start_date)} → {fmtDate(booking.end_date)}</Descriptions.Item>
          </Descriptions>
        )}
        <Form form={dispatchForm} layout="vertical">
          <Form.Item name="quantity" label={`Qty dispatched (blank = all ${dispatchTarget?.quantity})`}>
            <InputNumber min={1} max={dispatchTarget?.quantity} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Optional dispatch notes" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Return — ${returnTarget ? lineItemName(returnTarget) : ''}`}
        open={!!returnTarget}
        onCancel={() => { returnForm.resetFields(); setReturnTarget(null); setReturnQty(null); }}
        onOk={handleReturn}
        confirmLoading={returning}
        okText="Confirm Return"
        destroyOnClose
      >
        {returnTarget && (
          <Descriptions size="small" style={{ marginBottom: 16 }} bordered>
            <Descriptions.Item label="Dispatched">{returnTarget.dispatched_quantity}</Descriptions.Item>
          </Descriptions>
        )}
        <Form form={returnForm} layout="vertical" onValuesChange={(c) => { if ('quantity' in c) setReturnQty(c.quantity); }}>
          <Form.Item name="quantity" label={`Qty returned (blank = all ${returnTarget?.dispatched_quantity})`}>
            <InputNumber min={0} max={returnTarget?.dispatched_quantity} style={{ width: '100%' }} />
          </Form.Item>
          {returnQty !== null && returnQty < (returnTarget?.dispatched_quantity ?? 0) && (
            <>
              <Alert type="warning" showIcon
                message={`${(returnTarget?.dispatched_quantity ?? 0) - returnQty} unit(s) unaccounted — missing units will be written off.`}
                style={{ marginBottom: 12 }}
              />
              <Form.Item name="discrepancy_reason" label="Reason" rules={[{ required: true, message: 'Required when returning fewer than dispatched' }]}>
                <Select options={[{ value: 'damaged', label: 'Damaged' }, { value: 'lost', label: 'Lost' }, { value: 'other', label: 'Other' }]} />
              </Form.Item>
              <Form.Item name="discrepancy_note" label="Discrepancy note">
                <Input.TextArea rows={2} />
              </Form.Item>
            </>
          )}
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Optional return notes" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ─── Booking Timeline (expandable row) ───────────────────────────────────────

const BookingTimeline = ({ reservationId, useMovements = false }) => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!reservationId) return;
    setLoading(true);
    const fetch$ = useMovements
      ? inventoryService.getMovements(reservationId).then(res => res.movements || [])
      : inventoryService.getTransactions({ reservationId }).then(res => {
          const data = Array.isArray(res) ? res : res.transactions || [];
          return [...data].reverse();
        });
    fetch$
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [reservationId, useMovements]);

  if (loading) return (
    <div style={{ padding: '16px 24px' }}>
      <Text type="secondary">Loading movement history…</Text>
    </div>
  );

  if (!rows?.length) return (
    <div style={{ padding: '16px 24px' }}>
      <Text type="secondary">No movements recorded yet.</Text>
    </div>
  );

  if (useMovements) {
    const timelineItems = rows.map((r) => {
      const isWriteOff = r.transaction_type === 'out';
      const isReturn = !isWriteOff && r.to_location_name === 'Festivals Malta';
      const label = isWriteOff ? 'Written Off' : isReturn ? 'Returned' : 'Dispatched';
      const dotColor = isWriteOff ? 'red' : isReturn ? 'green' : 'blue';
      const qtyColor = isWriteOff ? '#f5222d' : isReturn ? '#52c41a' : '#1677ff';
      const route = r.from_location_name && r.to_location_name
        ? `${r.from_location_name} → ${r.to_location_name}`
        : r.from_location_name || r.to_location_name || null;
      return {
        color: dotColor,
        children: (
          <div style={{ paddingBottom: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}>
              <Space size={6}>
                <Text strong style={{ color: qtyColor }}>{label}</Text>
                <Tag color={dotColor === 'blue' ? 'blue' : dotColor === 'green' ? 'success' : 'error'}>
                  ×{r.quantity}
                </Tag>
              </Space>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
              </Text>
            </div>
            {route && (
              <div style={{ marginTop: 2 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <EnvironmentOutlined style={{ marginRight: 4 }} />{route}
                </Text>
              </div>
            )}
            <div style={{ marginTop: 2 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>{r.done_by}</Text>
            </div>
            {r.notes && (
              <div style={{ marginTop: 6, padding: '6px 10px', background: '#f5f5f5', borderRadius: 6, borderLeft: `3px solid ${qtyColor}` }}>
                <Text style={{ fontSize: 12 }}>{r.notes}</Text>
              </div>
            )}
          </div>
        ),
      };
    });

    return (
      <div style={{ padding: '16px 32px 8px' }}>
        <Timeline items={timelineItems} />
      </div>
    );
  }

  // Admin / management view
  const timelineItems = rows.map((r) => {
    const isDiscrepancy = r.transaction_type === 'adjust' && r.quantity < 0;
    const dotColor = isDiscrepancy || r.transaction_type === 'out' ? 'red'
      : r.transaction_type === 'in' ? 'green' : 'blue';
    const movement = r.from_location_name && r.to_location_name
      ? `${r.from_location_name} → ${r.to_location_name}`
      : r.from_location_name || r.to_location_name || null;
    const by = r.scanner_name || r.submitted_by || '—';
    return {
      color: dotColor,
      children: (
        <div style={{ paddingBottom: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}>
            <Space size={6}>
              <Tag color={typeTagColor[r.transaction_type] || 'default'}>
                {r.transaction_type?.toUpperCase()}
              </Tag>
              <Text strong>×{Math.abs(r.quantity)}</Text>
              {isDiscrepancy && <Tag color="red" style={{ fontSize: 10 }}>write-off</Tag>}
            </Space>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
            </Text>
          </div>
          {movement && (
            <div style={{ marginTop: 2 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <EnvironmentOutlined style={{ marginRight: 4 }} />{movement}
              </Text>
            </div>
          )}
          <div style={{ marginTop: 2 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>{by}</Text>
          </div>
          {r.notes && (
            <div style={{ marginTop: 6, padding: '6px 10px', background: '#f5f5f5', borderRadius: 6, borderLeft: '3px solid #d9d9d9' }}>
              <Text style={{ fontSize: 12 }}>{r.notes}</Text>
            </div>
          )}
        </div>
      ),
    };
  });

  return (
    <div style={{ padding: '16px 32px 8px' }}>
      <Timeline items={timelineItems} />
    </div>
  );
};

// ─── Public Items Tab (all users) ────────────────────────────────────────────

const PublicItemsTab = ({ section = 'catalogue', onSummary }) => {
  const { user } = useAuth();
  const isAdmin = user?.isAdmin;
  const isInventoryManager = user?.isInventoryManager;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [editBookingTarget, setEditBookingTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await inventoryService.getPublicItems();
      setItems(Array.isArray(res) ? res : res.items || []);
    } catch {
      message.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBookings = useCallback(async () => {
    try {
      setBookingsLoading(true);
      const res = await inventoryService.getBookings({ mine: true });
      setBookings(Array.isArray(res) ? res : res.bookings || []);
    } catch {
      // silent
    } finally {
      setBookingsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    fetchBookings();
  }, [fetchItems, fetchBookings]);

  useInventoryEvents({
    onItem: () => fetchItems(),
    // Creating/editing/cancelling/dispatching/returning a booking also moves
    // stock — so the availability numbers in the catalogue need a refresh too,
    // not just the "My Bookings" list.
    onBooking: () => { fetchBookings(); fetchItems(); },
  });

  const cartIds = new Set(cart.map(i => i.item_id));

  // Counts for the page rail (display only)
  useEffect(() => {
    onSummary?.({
      items: items.length,
      activeBookings: bookings.filter(b => ['active', 'in_progress', 'out'].includes(b.status)).length,
    });
  }, [items, bookings, onSummary]);

  // Search and location filter narrow what is shown; the data is unchanged
  const primaryLocations = useMemo(
    () => [...new Set(items.map(i => i.primary_location).filter(Boolean))].sort(),
    [items]
  );
  const visibleItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter(i =>
      (!locationFilter || i.primary_location === locationFilter)
      && (!term || [i.name, i.barcode, i.description].filter(Boolean).join(' ').toLowerCase().includes(term))
    );
  }, [items, search, locationFilter]);

  const handleAddToCart = (item) => {
    if (cartIds.has(item.item_id)) {
      setCart(prev => prev.filter(i => i.item_id !== item.item_id));
    } else {
      setCart(prev => [...prev, item]);
      message.success(`${item.name} added to booking`);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      const res = await inventoryService.cancelBooking(bookingId);
      message.success(`${res.cancelled ?? 'All'} item(s) cancelled`);
      fetchBookings();
      fetchItems();
    } catch (err) {
      message.error(err.message || 'Failed to cancel booking');
    }
  };

  const itemColumns = [
    {
      title: 'Item',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, record) => (
        <div className="inv-item">
          {record.image_path ? (
            <Image
              src={`${IMAGE_BASE}${record.image_path}`}
              width={44}
              height={44}
              className="inv-thumb-img"
              preview={{ src: `${IMAGE_BASE}${record.image_path}` }}
            />
          ) : (
            <span className="inv-thumb" aria-hidden="true"><InboxOutlined /></span>
          )}
          <span className="inv-item__text">
            <span className="inv-item__name">{name}</span>
            <span className="inv-item__meta">
              {record.barcode && <span className="num">{record.barcode}</span>}
              {record.primary_location && (
                <span className="inv-loc"><EnvironmentOutlined aria-hidden="true" />{record.primary_location}</span>
              )}
            </span>
          </span>
        </div>
      ),
    },
    {
      title: 'Today',
      key: 'available_today',
      width: 250,
      sorter: (a, b) => (a.available_today ?? a.available_quantity ?? 0) - (b.available_today ?? b.available_quantity ?? 0),
      render: (_, record) => {
        const available = Number(record.available_today ?? record.available_quantity ?? 0);
        const reserved = Number(record.reserved_quantity ?? 0);
        const total = Math.max(Number(record.current_quantity) || 0, available + reserved);
        const pct = (n) => (total > 0 ? `${(n / total) * 100}%` : '0%');
        return (
          <div className="inv-meter" aria-label={`${available} available today, ${reserved} reserved`}>
            <div className="inv-meter__bar">
              {available > 0 && <i className="is-free" style={{ width: pct(available) }} />}
              {reserved > 0 && <i className="is-held" style={{ width: pct(reserved) }} />}
            </div>
            <div className="inv-meter__nums num">
              <span className={available > 0 ? '' : 'is-zero'}><b>{available}</b> available</span>
              <span><b>{reserved}</b> reserved</span>
            </div>
          </div>
        );
      },
    },
    {
      title: 'In store',
      dataIndex: 'primary_quantity',
      key: 'primary_quantity',
      width: 100,
      align: 'right',
      sorter: (a, b) => (a.primary_quantity ?? 0) - (b.primary_quantity ?? 0),
      render: (v) => <span className="inv-qty num">{v ?? 0}</span>,
    },
    {
      title: '',
      key: 'cart',
      width: 170,
      align: 'right',
      fixed: 'right',
      render: (_, record) => {
        const inCart = cartIds.has(record.item_id);
        return inCart ? (
          <Tooltip title="Remove from booking">
            <Button className="inv-added" icon={<CheckOutlined />} onClick={() => handleAddToCart(record)}
              aria-label={`Remove ${record.name} from booking`}>
              In booking
            </Button>
          </Tooltip>
        ) : (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAddToCart(record)}>
            Add to booking
          </Button>
        );
      },
    },
  ];

  const bookingColumns = [
    {
      title: 'Booking',
      dataIndex: 'booking_id',
      key: 'booking_id',
      width: 110,
      render: (v, r) => (
        <span className="inv-booking">
          <span className="inv-ref num">#{v}</span>
          <small className="num">{r.line_count} item{r.line_count !== 1 ? 's' : ''}</small>
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 170,
      sorter: (a, b) => (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9),
      defaultSortOrder: 'ascend',
      render: renderBookingStatus,
    },
    {
      title: 'Dates',
      key: 'dates',
      width: 180,
      render: (_, r) => fmtRange(r.start_date, r.end_date)
        ? <span className="num">{fmtRange(r.start_date, r.end_date)}</span>
        : <Text type="secondary">—</Text>,
    },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', ellipsis: true, render: (v) => v || <Text type="secondary">—</Text> },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      render: (v) => v ? new Date(v).toLocaleDateString() : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 250,
      fixed: 'right',
      render: (_, record) => {
        const canEditBooking = record.status === 'active';
        const canCancelBooking = record.status === 'active' || record.status === 'in_progress';
        return (
          <Space wrap>
            <ViewPdfButton bookingId={record.booking_id} />
            {canEditBooking && (
              <Button size="small" icon={<EditOutlined />} onClick={() => setEditBookingTarget(record)}>
                Edit
              </Button>
            )}
            {canCancelBooking && (
              <Popconfirm
                title="Cancel this booking? All active items will be cancelled."
                onConfirm={() => handleCancelBooking(record.booking_id)}
                okText="Cancel Booking"
                okButtonProps={{ danger: true }}
              >
                <Button size="small" danger>Cancel</Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <section hidden={section !== 'catalogue'}>
        <header className="inv-head">
          <div>
            <h1 className="inv-title">Catalogue</h1>
            <p className="inv-sub">Add items to a booking, then choose dates and quantities.</p>
          </div>
        </header>
        <div className="inv-toolbar">
          <Input
            allowClear
            className="inv-search"
            prefix={<SearchOutlined aria-hidden="true" />}
            placeholder="Search by name or barcode"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search items"
          />
          <Select
            allowClear
            className="inv-filter"
            placeholder="All locations"
            value={locationFilter}
            onChange={(v) => setLocationFilter(v ?? null)}
            options={primaryLocations.map(l => ({ value: l, label: l }))}
            aria-label="Primary location"
          />
          <Button icon={<ReloadOutlined />} onClick={() => { fetchItems(); fetchBookings(); }}>Refresh</Button>
        </div>

        <Table
          className="inv-table"
          dataSource={visibleItems}
          columns={itemColumns}
          rowKey="item_id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          rowClassName={(r) => (cartIds.has(r.item_id) ? 'is-in-booking' : '')}
          locale={{ emptyText: items.length ? 'No items match your search.' : 'No items in the catalogue yet.' }}
        />
      </section>

      <section hidden={section !== 'bookings'}>
        <header className="inv-head">
          <div>
            <h1 className="inv-title">My bookings</h1>
            <p className="inv-sub">Open a booking to see its items, locations and history.</p>
          </div>
          <Button icon={<ReloadOutlined />} onClick={fetchBookings}>Refresh</Button>
        </header>
        <Table
          className="inv-table"
          dataSource={bookings}
          columns={bookingColumns}
          rowKey="booking_id"
          loading={bookingsLoading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 960 }}
          locale={{ emptyText: 'You have no bookings yet.' }}
          expandable={{
            expandedRowRender: (record) => (
              <BookingLinesTable
                bookingId={record.booking_id}
                canEdit
                canActOnLifecycle={isAdmin || isInventoryManager}
                useMovements
                onBookingChange={fetchBookings}
              />
            ),
            rowExpandable: () => true,
          }}
        />
      </section>

      {cart.length > 0 && (
        <div className="inv-tray" role="region" aria-label="Your booking">
          <div className="inv-tray__stack" aria-hidden="true">
            {cart.slice(0, 4).map(i => (
              i.image_path
                ? <img key={i.item_id} src={`${IMAGE_BASE}${i.image_path}`} alt="" />
                : <span key={i.item_id}><InboxOutlined /></span>
            ))}
          </div>
          <p className="inv-tray__text">
            <b>{cart.length} item{cart.length !== 1 ? 's' : ''} in your booking</b>
            <small>{cart.map(i => i.name).join(', ')}</small>
          </p>
          <Button type="primary" size="large" icon={<ShoppingCartOutlined />} onClick={() => setCartOpen(true)}>
            Review booking
          </Button>
        </div>
      )}

      <CartBookingModal
        cart={cart}
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        onSuccess={(result) => {
          setCartOpen(false);
          const createdIds = new Set((result.created || []).map(c => c.item_id));
          setCart(prev => prev.filter(i => !createdIds.has(i.item_id)));
          fetchBookings();
          fetchItems();
        }}
      />

      <EditBookingModal
        booking={editBookingTarget}
        open={!!editBookingTarget}
        onClose={() => setEditBookingTarget(null)}
        onSuccess={() => { setEditBookingTarget(null); fetchBookings(); }}
      />
    </>
  );
};

// ─── Reservations Management Tab (admin/manager view) ─────────────────────────

const ReservationsManagementTab = () => {
  const { user } = useAuth();
  const isAdmin = user?.isAdmin;
  const isInventoryManager = user?.isInventoryManager;
  const canActOnLifecycle = isAdmin || isInventoryManager;
  const currentUserId = user?.userId || user?.id;
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editBookingTarget, setEditBookingTarget] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await inventoryService.getBookings();
      setBookings(Array.isArray(res) ? res : res.bookings || []);
    } catch {
      message.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  useInventoryEvents({
    onBooking: () => fetchBookings(),
  });

  const handleCancelBooking = async (booking) => {
    try {
      const res = await inventoryService.cancelBooking(booking.booking_id);
      message.success(`${res.cancelled ?? 'All'} item(s) cancelled`);
      fetchBookings();
    } catch (err) {
      message.error(err.message || 'Failed to cancel booking');
    }
  };

  const columns = [
    {
      title: 'Booking',
      dataIndex: 'booking_id',
      key: 'booking_id',
      width: 110,
      render: (v, r) => (
        <span className="inv-booking">
          <span className="inv-ref num">#{v}</span>
          <small className="num">{r.line_count} item{r.line_count !== 1 ? 's' : ''}</small>
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 170,
      sorter: (a, b) => (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9),
      render: renderBookingStatus,
    },
    { title: 'Reserved By', dataIndex: 'reserved_by_name', key: 'reserved_by_name', width: 190, render: (v) => v || <Text type="secondary">—</Text> },
    {
      title: 'Dates',
      key: 'dates',
      width: 180,
      render: (_, r) => fmtRange(r.start_date, r.end_date)
        ? <span className="num">{fmtRange(r.start_date, r.end_date)}</span>
        : <Text type="secondary">—</Text>,
    },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', ellipsis: true, render: (v) => v || <Text type="secondary">—</Text> },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (v) => v ? new Date(v).toLocaleString() : '—',
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 250,
      fixed: 'right',
      render: (_, record) => {
        const canEdit = record.status === 'active' && (canActOnLifecycle || record.reserved_by === currentUserId);
        const canCancel = (record.status === 'active' || record.status === 'in_progress') && (canActOnLifecycle || record.reserved_by === currentUserId);
        return (
          <Space wrap>
            <ViewPdfButton bookingId={record.booking_id} />
            {canEdit && (
              <Button size="small" icon={<EditOutlined />} onClick={() => setEditBookingTarget(record)}>
                Edit
              </Button>
            )}
            {canCancel && (
              <Popconfirm
                title="Cancel this booking? All active items will be cancelled."
                onConfirm={() => handleCancelBooking(record)}
                okText="Cancel Booking"
                okButtonProps={{ danger: true }}
              >
                <Button size="small" danger>Cancel</Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  // Status filter narrows what is shown; the data is unchanged
  const statusCounts = bookings.reduce((acc, b) => ({ ...acc, [b.status]: (acc[b.status] || 0) + 1 }), {});
  const statusOptions = [
    { value: 'all', label: `All ${bookings.length}` },
    ...['active', 'in_progress', 'out', 'returned', 'cancelled'].map(s => ({
      value: s,
      label: `${BOOKING_STATUS[s].label} ${statusCounts[s] || 0}`,
    })),
  ];
  const visibleBookings = statusFilter === 'all' ? bookings : bookings.filter(b => b.status === statusFilter);

  return (
    <>
      <div className="inv-toolbar">
        <Segmented value={statusFilter} onChange={setStatusFilter} options={statusOptions} className="inv-seg" />
        <Button icon={<ReloadOutlined />} onClick={fetchBookings}>Refresh</Button>
      </div>
      <Table
        className="inv-table"
        dataSource={visibleBookings}
        columns={columns}
        rowKey="booking_id"
        loading={loading}
        pagination={{ pageSize: 25 }}
        scroll={{ x: 1180 }}
        expandable={{
          expandedRowRender: (record) => (
            <BookingLinesTable
              bookingId={record.booking_id}
              canEdit={canActOnLifecycle || record.reserved_by === currentUserId}
              canActOnLifecycle={canActOnLifecycle}
              useMovements={false}
              onBookingChange={fetchBookings}
            />
          ),
          rowExpandable: () => true,
        }}
      />

      <EditBookingModal
        booking={editBookingTarget}
        open={!!editBookingTarget}
        onClose={() => setEditBookingTarget(null)}
        onSuccess={() => { setEditBookingTarget(null); fetchBookings(); }}
      />
    </>
  );
};

// ─── Locations Tab ───────────────────────────────────────────────────────────

const locationStatusOrder = { pending: 0, approved: 1, declined: 2 };

const LocationsTab = () => {
  const { user } = useAuth();
  const canReview = user?.isAdmin || user?.isInventoryManager;
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actioningId, setActioningId] = useState(null);

  // Create modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [creating, setCreating] = useState(false);

  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // Decline modal
  const [declineTarget, setDeclineTarget] = useState(null);
  const [declineForm] = Form.useForm();
  const [declining, setDeclining] = useState(false);

  const fetchLocations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await inventoryService.getLocations();
      setLocations(Array.isArray(res) ? res : res.locations || []);
    } catch (err) {
      message.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // Approvals/declines happen from anywhere (or by another admin) — keep this list live.
  useInventoryEvents({ onLocation: () => fetchLocations() });

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreating(true);
      await inventoryService.createLocation(values);
      message.success('Location created');
      setCreateModalOpen(false);
      createForm.resetFields();
      fetchLocations();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Failed to create location');
    } finally {
      setCreating(false);
    }
  };

  const openEditModal = (location) => {
    setEditingLocation(location);
    editForm.setFieldsValue({
      name: location.name,
      description: location.description,
      is_active: location.is_active,
      is_primary: location.is_primary || false,
    });
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    try {
      const values = await editForm.validateFields();
      setSaving(true);
      await inventoryService.updateLocation(editingLocation.location_id, values);
      message.success('Location updated');
      setEditModalOpen(false);
      fetchLocations();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Failed to update location');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (location) => {
    try {
      await inventoryService.deleteLocation(location.location_id);
      message.success(`Location "${location.name}" deactivated`);
      fetchLocations();
    } catch (err) {
      message.error('Failed to deactivate location');
    }
  };

  const handleApprove = async (location) => {
    setActioningId(location.location_id);
    try {
      await inventoryService.approveLocation(location.location_id);
      message.success(`"${location.name}" approved — it's bookable now`);
      fetchLocations();
    } catch (err) {
      message.error(err.status === 409 ? 'This location was already reviewed' : (err.message || 'Failed to approve location'));
      if (err.status === 409) fetchLocations();
    } finally {
      setActioningId(null);
    }
  };

  const handleDecline = async () => {
    try {
      const values = await declineForm.validateFields();
      setDeclining(true);
      await inventoryService.declineLocation(declineTarget.location_id, values.reason || undefined);
      message.success(`"${declineTarget.name}" declined`);
      setDeclineTarget(null);
      declineForm.resetFields();
      fetchLocations();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.status === 409 ? 'This location was already reviewed' : (err.message || 'Failed to decline location'));
      if (err.status === 409) { setDeclineTarget(null); fetchLocations(); }
    } finally {
      setDeclining(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, record) =>
        record.status === 'declined' ? (
          <Tooltip title={record.decline_reason ? `Declined: ${record.decline_reason}` : 'Declined'}>
            <Text delete type="secondary">{name}</Text>
          </Tooltip>
        ) : (
          <Text>{name}</Text>
        ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Primary',
      dataIndex: 'is_primary',
      key: 'is_primary',
      width: 80,
      render: (v) => v ? <Tag color="gold">Primary</Tag> : null,
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      sorter: (a, b) => (locationStatusOrder[a.status] ?? 1) - (locationStatusOrder[b.status] ?? 1),
      render: (_, record) => {
        if (record.status === 'pending') return <Tag color="orange">Pending review</Tag>;
        if (record.status === 'declined') return <Tag color="red">Declined</Tag>;
        return record.is_active ? <Badge status="success" text="Active" /> : <Badge status="error" text="Inactive" />;
      },
    },
    {
      title: 'Requested By',
      key: 'requested_by',
      width: 150,
      render: (_, record) => record.created_by_name || <Text type="secondary">—</Text>,
    },
    {
      title: 'Reviewed',
      key: 'reviewed',
      width: 170,
      render: (_, record) => {
        if (!record.reviewed_by_name && !record.reviewed_at) return <Text type="secondary">—</Text>;
        return (
          <Text style={{ fontSize: 12 }}>
            {record.reviewed_by_name}
            {record.reviewed_at && <><br /><Text type="secondary" style={{ fontSize: 11 }}>{new Date(record.reviewed_at).toLocaleString()}</Text></>}
          </Text>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, record) => {
        if (record.status === 'pending') {
          if (!canReview) return <Text type="secondary" style={{ fontSize: 12 }}>Awaiting review</Text>;
          return (
            <Space wrap>
              <Button
                size="small" type="primary" icon={<CheckCircleOutlined />}
                loading={actioningId === record.location_id}
                onClick={() => handleApprove(record)}
              >
                Approve
              </Button>
              <Button
                size="small" danger icon={<CloseCircleOutlined />}
                onClick={() => setDeclineTarget(record)}
              >
                Decline
              </Button>
            </Space>
          );
        }
        if (record.status === 'declined') return null;
        return (
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)}>
              Edit
            </Button>
            <Popconfirm
              title={`Deactivate "${record.name}"?`}
              description="This location will be hidden from the scanner dropdown."
              onConfirm={() => handleDeactivate(record)}
              okText="Deactivate"
              okButtonProps={{ danger: true }}
              disabled={!record.is_active}
            >
              <Button
                size="small"
                danger
                icon={<StopOutlined />}
                disabled={!record.is_active}
              >
                Deactivate
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Button icon={<ReloadOutlined />} onClick={fetchLocations}>
          Refresh
        </Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
          New Location
        </Button>
      </div>

      <Table
        dataSource={locations}
        columns={columns}
        rowKey="location_id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 'max-content' }}
        onRow={(record) => ({ style: record.status === 'pending' ? { background: '#fafafa' } : undefined })}
      />

      {/* Create Location Modal */}
      <Modal
        title="New Location"
        open={createModalOpen}
        onCancel={() => { setCreateModalOpen(false); createForm.resetFields(); }}
        onOk={handleCreate}
        confirmLoading={creating}
        okText="Create"
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="e.g. Stage A" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="e.g. Main stage storage" />
          </Form.Item>
          <Form.Item name="is_primary" label="Set as Primary Location" initialValue={false}>
            <Select
              options={[
                { value: false, label: 'No' },
                { value: true, label: 'Yes — make this the primary location' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Location Modal */}
      <Modal
        title="Edit Location"
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={handleEditSave}
        confirmLoading={saving}
        okText="Save Changes"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="is_active" label="Status">
            <Select
              options={[
                { value: 1, label: 'Active' },
                { value: 0, label: 'Inactive (hidden from scanners)' },
              ]}
            />
          </Form.Item>
          <Form.Item name="is_primary" label="Primary Location">
            <Select
              options={[
                { value: false, label: 'No' },
                { value: true, label: 'Yes — set as primary location' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Decline Location Modal */}
      <Modal
        title={`Decline "${declineTarget?.name || ''}"`}
        open={!!declineTarget}
        onCancel={() => { setDeclineTarget(null); declineForm.resetFields(); }}
        onOk={handleDecline}
        confirmLoading={declining}
        okText="Decline Location"
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Declined locations stay on record but can never be used for bookings. This can't be undone from here.
        </Text>
        <Form form={declineForm} layout="vertical">
          <Form.Item name="reason" label="Reason (optional, visible to other admins)">
            <Input.TextArea rows={3} placeholder="e.g. Duplicate of an existing location" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ─── Stock By Location Tab ───────────────────────────────────────────────────

const StockByLocationTab = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({});

  const load = useCallback(async (activeFilters) => {
    setLoading(true);
    try {
      const res = await inventoryService.getStockByLocation(activeFilters);
      setData(res.locations || []);
    } catch (e) {
      message.error('Failed to load stock by location');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(filters);
  }, [load, filters]);

  useEffect(() => {
    inventoryService.getLocations()
      .then(r => setLocations(Array.isArray(r) ? r : r.locations || []))
      .catch(() => {});
    inventoryService.getItems()
      .then(r => setItems(Array.isArray(r) ? r : r.items || []))
      .catch(() => {});
  }, []);

  return (
    <>
      <Space style={{ marginBottom: 20 }} wrap>
        <Select
          allowClear
          placeholder="Filter by location"
          style={{ minWidth: 200 }}
          options={locations.map((l) => ({ value: l.location_id, label: l.name }))}
          onChange={(v) => setFilters((f) => ({ ...f, locationId: v ?? undefined }))}
        />
        <Select
          allowClear
          placeholder="Filter by item"
          style={{ minWidth: 200 }}
          options={items.map((i) => ({ value: i.item_id, label: i.name }))}
          onChange={(v) => setFilters((f) => ({ ...f, itemId: v ?? undefined }))}
        />
        <Button icon={<ReloadOutlined />} onClick={() => load(filters)}>
          Refresh
        </Button>
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <Text type="secondary">Loading…</Text>
        </div>
      ) : data.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <Text type="secondary">No stock data found.</Text>
        </div>
      ) : (
        <Row gutter={[20, 20]}>
          {data.map((loc) => {
            const totalUnits = loc.items?.reduce((s, i) => s + (i.quantity ?? 0), 0) ?? 0;
            return (
              <Col xs={24} sm={24} md={12} lg={8} key={loc.location_id}>
                <Card
                  title={
                    <Space>
                      <EnvironmentOutlined style={{ color: '#1890ff' }} />
                      <span>{loc.location_name}</span>
                      <Tag color={totalUnits > 0 ? 'green' : totalUnits < 0 ? 'red' : 'default'}>
                        {totalUnits > 0 ? '+' : ''}{totalUnits} units
                      </Tag>
                    </Space>
                  }
                  size="small"
                  bodyStyle={{ padding: 0 }}
                >
                  {(loc.items || []).map((item, idx) => (
                    <div
                      key={item.item_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 16px',
                        borderTop: idx === 0 ? 'none' : '1px solid #f0f0f0',
                      }}
                    >
                      {item.image_path ? (
                        <Image
                          src={`${IMAGE_BASE}${item.image_path}`}
                          width={44}
                          height={44}
                          style={{ objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                          preview={{ src: `${IMAGE_BASE}${item.image_path}` }}
                        />
                      ) : (
                        <div style={{
                          width: 44, height: 44, borderRadius: 6, flexShrink: 0,
                          background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <InboxOutlined style={{ color: '#bbb', fontSize: 20 }} />
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.item_name}
                        </div>
                        <Text type="secondary" style={{ fontSize: 11 }}>{item.barcode}</Text>
                      </div>
                      <Tag
                        color={item.quantity > 0 ? 'green' : item.quantity < 0 ? 'red' : 'default'}
                        style={{ fontWeight: 700, fontSize: 14, padding: '2px 10px', flexShrink: 0 }}
                      >
                        {item.quantity > 0 ? '+' : ''}{item.quantity}
                      </Tag>
                    </div>
                  ))}
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const MANAGE_VIEWS = [
  { key: 'items',             label: 'Items',             icon: <ContainerTwoTone twoToneColor="#667085" />,   sub: 'Add items, print barcode labels and adjust stock.',            Component: ItemsTab },
  { key: 'transactions',      label: 'Transactions',      icon: <InteractionTwoTone twoToneColor="#667085" />, sub: 'Every stock movement, searchable by item, type and date.',      Component: TransactionsTab },
  { key: 'locations',         label: 'Locations',         icon: <EnvironmentTwoTone twoToneColor="#667085" />, sub: 'Stores and venues, including locations waiting for review.',    Component: LocationsTab },
  { key: 'stock-by-location', label: 'Stock by location', icon: <DatabaseTwoTone twoToneColor="#667085" />,    sub: 'How much of each item is at each location.',                   Component: StockByLocationTab },
  { key: 'scanners',          label: 'Scanners',          icon: <ScanOutlined />,                              sub: 'Barcode scanners and their access tokens.',                    Component: ScannersTab },
  { key: 'reservations',      label: 'Reservations',      icon: <ProfileTwoTone twoToneColor="#667085" />,     sub: 'Every booking across staff. Open one to dispatch or return items.', Component: ReservationsManagementTab },
];

const InventoryManagement = () => {
  const { user } = useAuth();
  const isInventoryManager = user?.isInventoryManager;
  const [view, setView] = useState('catalogue');
  // Views mount the first time they are opened and then stay mounted (hidden),
  // like the tabs they replace, so their data, cart and live updates persist.
  const [visited, setVisited] = useState(() => new Set(['catalogue']));
  const [summary, setSummary] = useState({ items: null, activeBookings: 0 });

  const open = (key) => {
    setView(key);
    setVisited(prev => (prev.has(key) ? prev : new Set(prev).add(key)));
  };

  const browseItems = [
    { key: 'catalogue', label: 'Catalogue',   icon: <AppstoreTwoTone twoToneColor="#2748d9" />, count: summary.items },
    { key: 'bookings',  label: 'My bookings', icon: <CarryOutTwoTone twoToneColor="#2748d9" />, count: summary.activeBookings ? `${summary.activeBookings} active` : null, accent: summary.activeBookings > 0 },
  ];
  const overviewOpen = view === 'catalogue' || view === 'bookings';

  const railButton = (item) => (
    <button
      key={item.key}
      type="button"
      className={`inv-rail__item${view === item.key ? ' is-on' : ''}${item.accent ? ' is-accent' : ''}`}
      aria-current={view === item.key ? 'page' : undefined}
      onClick={() => open(item.key)}
    >
      <span className="inv-rail__icon" aria-hidden="true">{item.icon}</span>
      <span className="inv-rail__text">{item.label}</span>
      {item.count != null && <span className="inv-rail__count num">{item.count}</span>}
    </button>
  );

  return (
    <DOTheme>
      <Layout className="do-shell do-scope inv-page">
        <NavigationBar />
        <main className="do-main inv-main">
          <aside className="inv-rail">
            <div className="inv-rail__head">
              <span className="inv-rail__name">Inventory</span>
            </div>
            <nav className="inv-rail__nav" aria-label="Inventory">
              <div className="inv-rail__group">
                <p className="inv-rail__label">Browse</p>
                {browseItems.map(railButton)}
              </div>
              {isInventoryManager && (
                <div className="inv-rail__group">
                  <p className="inv-rail__label">Manage</p>
                  {MANAGE_VIEWS.map(railButton)}
                </div>
              )}
            </nav>
          </aside>

          <div className="inv-sheet">
            {(visited.has('catalogue') || visited.has('bookings')) && (
              <div hidden={!overviewOpen}>
                <PublicItemsTab section={view === 'bookings' ? 'bookings' : 'catalogue'} onSummary={setSummary} />
              </div>
            )}
            {isInventoryManager && MANAGE_VIEWS.map(({ key, label, sub, Component }) => (
              visited.has(key) && (
                <div key={key} hidden={view !== key}>
                  <header className="inv-head">
                    <div>
                      <h1 className="inv-title">{label}</h1>
                      <p className="inv-sub">{sub}</p>
                    </div>
                  </header>
                  <Component />
                </div>
              )
            ))}
          </div>
        </main>
      </Layout>
    </DOTheme>
  );
};

export default InventoryManagement;
